/**
 * Country Guides
 * Hitchhiking guides by country
 */

import { getGuideSections, loadGuideSections } from './guideSectionsLoader.js'

export const countryGuides = [
  {
    code: 'FR',
    name: 'France',
    nameEn: 'France',
    nameEs: 'Francia',
    nameDe: 'Frankreich',
    flag: '🇫🇷',
    legality: 'legal',
    legalityText: 'Interdit sur autoroutes et bretelles, mais les aires de péage et stations-service sont autorisées',
    legalityTextEn: 'Prohibited on motorways and slip roads, but toll areas and gas stations are allowed',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 45,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Progressez de barrière de péage en barrière de péage pour les longues distances',
      'Les stations-service d\'autoroute sont les meilleurs spots, parlez directement aux conducteurs',
      'Achetez un petit café pour devenir client si le personnel vous demande de partir',
      'La plupart des Français parlent peu anglais, quelques mots de français changent tout',
      'Les plaques montrent les régions : 75=Paris, 13=Marseille, 69=Lyon, 33=Bordeaux',
      'La carte Michelin 726 est recommandée, cartes gratuites aux bureaux de péage',
      'Les conducteurs offrent souvent de l\'argent (5-60 EUR) ou des repas',
      'Le dimanche seuls les camions de surgelés circulent, camions limités à 90 km/h',
    ],
    tipsEn: [
      'Progress from toll barrier to toll barrier for long distances',
      'Highway gas stations are the best spots, talk directly to drivers',
      'Buy a cheap coffee to become a customer if staff asks you to leave',
      'Most French speak little English, a few French words make a huge difference',
      'License plates show regions: 75=Paris, 13=Marseille, 69=Lyon, 33=Bordeaux',
      'Michelin 726 map is recommended, free maps at toll offices',
      'Drivers often offer money (EUR 5-60) or meals',
      'Sundays: only frozen goods trucks allowed, trucks limited to 90 km/h',
    ],
    laws: [
      'L\'autostop sur les autoroutes elles-memes est techniquement interdit mais rarement verbalisé (~5%)',
      'Amende de 11-40 EUR en cas de verbalisation',
      'Les aires de repos, stations et zones de péage sont autorisées',
      'En Bretagne : pas de péages sur les autoroutes',
    ],
    lawsEn: [
      'Hitchhiking on the motorway itself is technically forbidden but rarely enforced (~5%)',
      'Fine of EUR 11-40 if caught',
      'Rest areas, gas stations and toll zones are allowed',
      'Brittany: no motorway tolls',
    ],
    phrases: [
      { local: 'Excusez-moi, je vais a [ville] en stop', meaning: 'Demander un trajet', meaningEn: 'Asking for a ride' },
      { local: 'Allez-vous dans cette direction ?', meaning: 'Vers la direction voulue', meaningEn: 'Going that direction?' },
      { local: 'Merci, bon voyage !', meaning: 'Remercier le conducteur', meaningEn: 'Thank the driver' },
    ],
    strategies: [
      'Positionnez-vous aux péages ou les conducteurs choisissent leur voie, juste avant la barriere',
      'Les panneaux avec un marqueur noir epais sont essentiels, indiquez des destinations intermédiaires',
      'Les stations-service aux sorties sont ideales pour aborder les conducteurs qui font le plein',
    ],
    strategiesEn: [
      'Position yourself at toll areas where drivers choose lanes, just before the barrier',
      'Signs with thick black markers are essential, write intermediate destinations',
      'Gas stations at exits are ideal for approaching drivers refueling',
    ],
    culturalNotes: 'La France a une tradition d\'autostop ancienne. Les conducteurs sont souvent généreux avec les autostoppeurs, offrant parfois de l\'argent ou un repas. Le contact visuel et un sourire sont essentiels.',
    culturalNotesEn: 'France has a long hitchhiking tradition. Drivers are often generous, sometimes offering money or meals. Eye contact and a smile are essential.',
    emergencyNumbers: { police: '17', ambulance: '15', fire: '18', universal: '112' },
    borderCrossings: [
      'Vers l\'Espagne : La Jonquera (Mediterranee) et Oyarzun/Irun (Atlantique) sont d\'excellents stops camions',
    ],
    borderCrossingsEn: [
      'To Spain: La Jonquera (Mediterranean) and Oyarzun/Irun (Atlantic) are excellent truck stops',
    ],
    events: [
      { name: 'Fete de la Musique', nameEn: 'Music Festival', date: '21 juin', dateEn: 'June 21', description: 'Concerts gratuits partout en France, beaucoup de monde sur les routes', descriptionEn: 'Free concerts across France, lots of road traffic', type: 'festival' },
      { name: 'Fete nationale', nameEn: 'Bastille Day', date: '14 juillet', dateEn: 'July 14', description: 'Feux d\'artifice partout, trafic tres charge la veille et le jour meme', descriptionEn: 'Fireworks everywhere, heavy traffic the day before and on the day', type: 'holiday' },
      { name: 'Tour de France', nameEn: 'Tour de France', date: 'Juillet', dateEn: 'July', description: 'Routes fermees sur le parcours, mais ambiance incroyable pour rencontrer des gens', descriptionEn: 'Road closures on the route, but amazing atmosphere to meet people', type: 'festival' },
      { name: 'Assomption', nameEn: 'Assumption Day', date: '15 aout', dateEn: 'August 15', description: 'Jour ferie, chassez-croise des vacances = trafic maximal sur autoroutes', descriptionEn: 'Public holiday, vacation crossover = maximum traffic on motorways', type: 'holiday' },
      { name: 'Toussaint', nameEn: 'All Saints Day', date: '1er novembre', dateEn: 'November 1', description: 'Vacances scolaires, beaucoup de families sur les routes', descriptionEn: 'School holidays, many families on the roads', type: 'holiday' },
    ],
    // Sections loaded dynamically by guideSectionsLoader.js
  },
  {
    code: 'DE',
    name: 'Allemagne',
    nameEn: 'Germany',
    nameEs: 'Alemania',
    nameDe: 'Deutschland',
    flag: '🇩🇪',
    legality: 'legal',
    legalityText: 'Légal partout sauf sur l\'Autobahn elle-meme et les bandes d\'arret d\'urgence',
    legalityTextEn: 'Legal everywhere except on the Autobahn itself and emergency lanes',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Restez toujours sur les Autobahns pour les longues distances',
      'Les Raststatten (aires avec stations) sont bien superieures aux simples aires de repos',
      'Aux feux rouges menant a l\'autoroute, frappez aux fenetres des voitures',
      'Les plaques montrent les regions : B=Berlin, M=Munich, HH=Hambourg',
      'Cartes gratuites aux stations-service, tank.rast.de pour la liste complete',
      'Plus de 95% parlent allemand, beaucoup parlent anglais ou francais',
      'Camions max 90 km/h, ideaux pour les trajets de nuit mais interdits le weekend avant 22h',
      'La region de la Ruhr (Dortmund, Essen) est difficile : peu de Raststatten',
    ],
    tipsEn: [
      'Always stick to the Autobahns for longer distances',
      'Raststatten (rest areas with petrol) are far superior to simple rest stops',
      'At traffic lights leading to the autobahn, knock on car windows',
      'License plates show regions: B=Berlin, M=Munich, HH=Hamburg',
      'Free maps at service stations, tank.rast.de for full listing',
      'Over 95% speak German, many speak English or French',
      'Trucks max 90km/h, great for night rides but restricted weekends before 10pm',
      'Ruhr area (Dortmund, Essen) is challenging: few Raststatten',
    ],
    laws: [
      'Interdit de se tenir sur l\'Autobahn ou les voies d\'urgence',
      'En tant qu\'etranger, vous devez porter votre passeport en permanence',
      'Les bretelles d\'acces et stations-service sont autorisees',
    ],
    lawsEn: [
      'Forbidden to stand on the Autobahn or emergency lanes',
      'As a foreigner you must carry passport at all times',
      'On-ramps and gas stations are allowed',
    ],
    phrases: [
      { local: 'Fahren Sie Richtung [Stadt]?', meaning: 'Allez-vous vers [ville] ?', meaningEn: 'Are you going toward [city]?' },
      { local: 'Danke schon!', meaning: 'Merci beaucoup', meaningEn: 'Thank you very much' },
      { local: 'Guten Tag', meaning: 'Bonjour', meaningEn: 'Hello' },
    ],
    strategies: [
      'Trois types de routes : Autobahn (panneaux bleus, sans limite), Bundesstrasse (jaunes, 100km/h), Landstrasse (campagne)',
      'Cherchez les feux rouges donnant sur les bretelles d\'autoroute pour aborder les conducteurs',
      'Les Raststatten desservent souvent les deux directions, verifiez le plan',
    ],
    strategiesEn: [
      'Three road types: Autobahn (blue signs, no limit), Bundesstrasse (yellow, 100km/h), Landstrasse (country)',
      'Look for traffic lights leading to highway ramps to approach drivers',
      'Raststatten often serve both directions, check the layout',
    ],
    culturalNotes: 'L\'Allemagne est une bonne destination pour l\'autostop. Les conducteurs sont ponctuels et fiables. Le reseau autoroutier est dense avec de nombreuses aires de service.',
    culturalNotesEn: 'Germany is a good hitchhiking destination. Drivers are punctual and reliable. The motorway network is dense with many service areas.',
    emergencyNumbers: { police: '110', ambulance: '112', fire: '112', universal: '112' },
    borderCrossings: [
      'Depuis Passau vers l\'est (Linz/Vienne) ou depuis Salzbourg vers le sud (Italie)',
      'La police allemande controle souvent pres de la frontiere tcheque',
    ],
    borderCrossingsEn: [
      'From Passau east (toward Linz/Vienna) or from Salzburg south (toward Italy)',
      'German police often check near Czech border',
    ],
    events: [
      { name: 'Karneval', nameEn: 'Carnival', date: 'Fevrier', dateEn: 'February', description: 'Cologne, Dusseldorf, Mainz : fete geante, transports bondes mais ambiance folle', descriptionEn: 'Cologne, Dusseldorf, Mainz: huge party, packed transport but crazy atmosphere', type: 'festival' },
      { name: 'Oktoberfest', nameEn: 'Oktoberfest', date: 'Sept-Oct', dateEn: 'Sept-Oct', description: 'Munich : 6 millions de visiteurs, ideal pour rencontrer des gens mais logement rare', descriptionEn: 'Munich: 6 million visitors, great for meeting people but accommodation scarce', type: 'festival' },
      { name: 'Tag der Deutschen Einheit', nameEn: 'German Unity Day', date: '3 octobre', dateEn: 'October 3', description: 'Fete nationale, celebrations dans la ville hote tournante', descriptionEn: 'National holiday, celebrations in the rotating host city', type: 'holiday' },
      { name: 'Weihnachtsmarkte', nameEn: 'Christmas Markets', date: 'Nov-Dec', dateEn: 'Nov-Dec', description: 'Marchés de Noel partout, bonne periode pour voyager entre villes', descriptionEn: 'Christmas markets everywhere, good time to travel between cities', type: 'festival' },
    ],
  },
  {
    code: 'ES',
    name: 'Espagne',
    nameEn: 'Spain',
    nameEs: 'España',
    nameDe: 'Spanien',
    flag: '🇪🇸',
    legality: 'legal',
    legalityText: 'Légal mais peu pratique. Interdit sur les autoroutes elles-memes',
    legalityTextEn: 'Legal but uncommon. Prohibited on motorways themselves',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 40,
    bestMonths: [3, 4, 5, 6, 9, 10, 11],
    tips: [
      'Le trafic diminue pendant la sieste (14h-17h), planifiez vos trajets en consequence',
      'Stations-service tous les 20-30 km, utilisez "Vas a...?" pour demander aux conducteurs',
      'Les autopistas (a peage) offrent de bons resultats meme la nuit aux peajes',
      'Les autovias (gratuites) ont des stations plus eloignees et moins frequentees',
      'Facile en Galice, Asturies, Estremadure ; difficile en Euskadi, Catalogne',
      'Comptez 300 km/jour la premiere fois, temps d\'attente souvent superieur a 1 heure',
      'L\'Andalousie (sud) est significativement plus difficile',
      'Plusieurs langues : catalan, basque, galicien. Villes avec noms doubles (Donostia/San Sebastian)',
    ],
    tipsEn: [
      'Traffic decreases during siesta (2pm-5pm), plan your trips accordingly',
      'Gas stations every 20-30km, use "Vas a...?" to ask drivers',
      'Autopistas (toll) have good success at peajes even at night',
      'Autovias (free) have stations further from road and less busy',
      'Easy in Galicia, Asturias, Extremadura; difficult in Euskadi, Catalonia',
      'Expect 300km/day first time, waiting times often over 1 hour',
      'Southern Spain (Andalusia) significantly more difficult',
      'Multiple languages: Catalan, Basque, Galician. Cities with dual names (Donostia/San Sebastian)',
    ],
    laws: [
      'L\'autostop est pratique principalement par les etrangers',
      'Facteurs historiques (dictature franquiste, affaire Alcasser) ont decourage la pratique',
      'Les stations-service et peajes sont les endroits les plus surs et legaux',
    ],
    lawsEn: [
      'Hitchhiking is primarily practiced by foreigners',
      'Historical factors (Francoist dictatorship, Alcasser case) discouraged the practice',
      'Gas stations and toll barriers are the safest and most legal spots',
    ],
    phrases: [
      { local: 'Hola! Vas a...?', meaning: 'Salut ! Tu vas a... ?', meaningEn: 'Hi! Going to...?' },
      { local: 'Gracias, buen viaje!', meaning: 'Merci, bon voyage !', meaningEn: 'Thanks, have a good trip!' },
      { local: 'Hago autoestop', meaning: 'Je fais de l\'autostop', meaningEn: 'I\'m hitchhiking' },
    ],
    strategies: [
      'Depuis la France : La Jonquera (Mediterranee) et Oyarzun/Irun (Atlantique) sont les principaux stops camions',
      'Les peajes sont excellents pour les trajets longue distance, meme de nuit',
      'L\'approche directe aux stations-service fonctionne mieux qu\'attendre avec un panneau',
    ],
    strategiesEn: [
      'From France: La Jonquera (Mediterranean) and Oyarzun/Irun (Atlantic) are major truck stops',
      'Toll barriers are excellent for long-distance rides, even at night',
      'Direct approach at gas stations works better than standing with a sign',
    ],
    culturalNotes: 'L\'autostop est peu commun en Espagne et pratique surtout par les etrangers. Les heures de sieste reduisent fortement le trafic. Un sourire et quelques mots d\'espagnol font la difference.',
    culturalNotesEn: 'Hitchhiking is uncommon in Spain, mostly practiced by foreigners. Siesta hours significantly reduce traffic. A smile and a few Spanish words make the difference.',
    emergencyNumbers: { police: '091', ambulance: '061', fire: '080', universal: '112' },
    borderCrossings: [
      'Depuis la France : La Jonquera (cote Mediterranee) et Irun (cote Atlantique)',
      'Vers le Portugal : nombreux postes-frontieres ouverts sur toute la frontiere',
    ],
    borderCrossingsEn: [
      'From France: La Jonquera (Mediterranean side) and Irun (Atlantic side)',
      'To Portugal: many open border crossings along the full border',
    ],
    events: [
      { name: 'San Fermin', nameEn: 'Running of the Bulls', date: '6-14 juillet', dateEn: 'July 6-14', description: 'Pampelune : trafic intense vers le nord, beaucoup de routards', descriptionEn: 'Pamplona: heavy traffic northbound, lots of backpackers', type: 'festival' },
      { name: 'La Tomatina', nameEn: 'La Tomatina', date: 'Dernier mercredi d\'aout', dateEn: 'Last Wednesday of August', description: 'Bunol (Valencia) : bataille de tomates, facile de trouver des lifts festifs', descriptionEn: 'Bunol (Valencia): tomato fight, easy to find festive rides', type: 'festival' },
      { name: 'Semana Santa', nameEn: 'Holy Week', date: 'Mars-Avril', dateEn: 'March-April', description: 'Processions dans toute l\'Espagne, trafic dense vers le sud (Seville, Malaga)', descriptionEn: 'Processions across Spain, heavy traffic southbound (Seville, Malaga)', type: 'holiday' },
      { name: 'Dia de la Hispanidad', nameEn: 'Hispanic Day', date: '12 octobre', dateEn: 'October 12', description: 'Fete nationale, beaucoup de deplacements interregionaux', descriptionEn: 'National holiday, lots of inter-regional travel', type: 'holiday' },
    ],
  },
  {
    code: 'IT',
    name: 'Italie',
    nameEn: 'Italy',
    nameEs: 'Italia',
    nameDe: 'Italien',
    flag: '🇮🇹',
    legality: 'restricted',
    legalityText: 'Art. 175 du code de la route interdit l\'autostop sur autoroutes, bretelles, jonctions et aires de service. Amende 21 EUR',
    legalityTextEn: 'Art. 175 road code forbids hitchhiking on motorways, ramps, junctions and service areas. Fine EUR 21',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 35,
    bestMonths: [4, 5, 6, 9, 10],
    tips: [
      'Positionnez-vous avant les panneaux "no autostop" a l\'entree des stations',
      'La police demande generalement juste de se deplacer plutot que de verbaliser',
      'Les Autogrill offrent cartes gratuites, toilettes gratuites et de bons spots',
      'Utilisez des destinations intermediaires (200-300 km) sur vos panneaux',
      'Pas de camions le weekend. Le lundi matin est le meilleur moment pour les camions',
      'Le nord est plus facile et plus sur que le sud',
      'Milan est difficile a quitter en autostop (peripherique complexe)',
    ],
    tipsEn: [
      'Position yourself before "no autostop" signs at station entrances',
      'Police typically ask to relocate rather than fine',
      'Autogrill offer free maps, free toilets and good hitching spots',
      'Use intermediate destinations (200-300km) on your signs',
      'No trucks on weekends. Early Monday best for truck rides',
      'North is easier and safer than south',
      'Milan is hard to leave by hitchhiking (complex ring road)',
    ],
    laws: [
      'Art. 175 du codice della strada interdit explicitement l\'autostop sur autoroutes et leurs dependances',
      'Amende de 21 EUR pour les autostoppeurs, jusqu\'a 137 EUR pour les conducteurs',
      'Contournement : se positionner avant les panneaux d\'interdiction aux entrees de stations',
    ],
    lawsEn: [
      'Art. 175 codice della strada explicitly forbids hitchhiking on motorways and related areas',
      'Fine EUR 21 for hitchhikers, up to EUR 137 for drivers',
      'Workaround: position before prohibition signs at station entrances',
    ],
    phrases: [
      { local: 'Faccio autostop', meaning: 'Je fais de l\'autostop', meaningEn: 'I\'m hitchhiking' },
      { local: 'Cerco un passaggio per...', meaning: 'Je cherche un trajet vers...', meaningEn: 'I\'m looking for a ride to...' },
      { local: 'Grazie mille!', meaning: 'Merci beaucoup !', meaningEn: 'Thank you so much!' },
    ],
    strategies: [
      'Les Autogrill sont les meilleurs spots : approche directe des conducteurs qui mangent ou font le plein',
      'Vers l\'Autriche par les Alpes : A23 ou A22 via le Brenner',
      'Verifiez l\'orientation des aires : certaines desservent une seule direction',
    ],
    strategiesEn: [
      'Autogrill are the best spots: directly approach drivers eating or refueling',
      'To Austria via Alps: A23 or A22 via Brennero',
      'Check service area orientation: some serve only one direction',
    ],
    culturalNotes: 'L\'autostop en Italie est moyen. Les Italiens sont chaleureux une fois que vous etes dans la voiture. Le terme local est "autostop" ou "passaggio".',
    culturalNotesEn: 'Hitchhiking in Italy is average. Italians are warm once you\'re in the car. The local term is "autostop" or "passaggio".',
    emergencyNumbers: { police: '113', ambulance: '118', fire: '115', universal: '112' },
    borderCrossings: [
      'Vers l\'Autriche : A22 via le Brenner ou A23',
      'Attention avec les camions vers la frontiere : la police peut vous demander de descendre',
    ],
    borderCrossingsEn: [
      'To Austria: A22 via Brennero or A23',
      'Caution with trucks at border: police may ask you to get out',
    ],
    events: [
      { name: 'Carnevale di Venezia', nameEn: 'Venice Carnival', date: 'Fevrier', dateEn: 'February', description: 'Venise : masques et costumes, trafic intense vers le nord-est', descriptionEn: 'Venice: masks and costumes, heavy traffic toward northeast', type: 'festival' },
      { name: 'Ferragosto', nameEn: 'Ferragosto', date: '15 aout', dateEn: 'August 15', description: 'Tout le pays en vacances, autoroutes bondees vers les cotes', descriptionEn: 'Whole country on holiday, motorways packed toward coasts', type: 'holiday' },
      { name: 'Palio di Siena', nameEn: 'Palio di Siena', date: '2 juillet & 16 aout', dateEn: 'July 2 & August 16', description: 'Course de chevaux historique a Sienne, ambiance medievale', descriptionEn: 'Historic horse race in Siena, medieval atmosphere', type: 'festival' },
    ],
  },
  {
    code: 'NL',
    name: 'Pays-Bas',
    nameEn: 'Netherlands',
    nameEs: 'Países Bajos',
    nameDe: 'Niederlande',
    flag: '🇳🇱',
    legality: 'legal',
    legalityText: 'Légal dans la plupart des endroits, interdit sur les autoroutes elles-memes',
    legalityTextEn: 'Legal in most places, prohibited on motorways themselves',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very Easy',
    avgWaitTime: 15,
    bestMonths: [4, 5, 6, 7, 8, 9],
    tips: [
      'Les stations-service sont les plus efficaces, attente de 5 a 45 minutes',
      'L\'approche directe des conducteurs bat le panneau a la main',
      'Certaines villes universitaires ont des "liftershaltes" officiels (spots d\'autostop designes)',
      'Neerlandais ou anglais fonctionnent le mieux. Les Neerlandais evitent parfois de repondre en allemand',
      'Achetez un cafe si on vous demande de partir, cela fait de vous un client',
      'La plupart des trajets font seulement 50 km en moyenne',
      'Depuis 1991, les etudiants ont des cartes de transport gratuites, reduisant la culture de l\'autostop',
    ],
    tipsEn: [
      'Gas stations are most effective, wait 5-45 minutes',
      'Direct approach to drivers beats standing with a sign',
      'Some university towns have official "liftershaltes" (designated hitchhiking spots)',
      'Dutch or English work best. Dutch sometimes avoid responding to German speakers',
      'Buy a coffee if asked to leave, it makes you a customer',
      'Most lifts average only 50km',
      'Since 1991, students get free transport cards, reducing hitchhiking culture',
    ],
    laws: [
      'Interdit de se tenir sur les autoroutes',
      'Les stations-service et bretelles d\'acces sont autorisees',
      'Certaines stations Shell peuvent etre problematiques (propriete privee)',
    ],
    lawsEn: [
      'Prohibited to stand on motorways',
      'Gas stations and on-ramps are allowed',
      'Some Shell stations may be problematic (private property)',
    ],
    phrases: [
      { local: 'Gaat u richting [stad]?', meaning: 'Allez-vous vers [ville] ?', meaningEn: 'Are you going toward [city]?' },
      { local: 'Dank u wel!', meaning: 'Merci beaucoup !', meaningEn: 'Thank you very much!' },
      { local: 'Goedendag', meaning: 'Bonjour', meaningEn: 'Good day' },
    ],
    culturalNotes: 'Les Pays-Bas sont un des meilleurs pays d\'Europe pour l\'autostop. Tout le monde parle anglais. Les distances sont courtes et le pays est tres sur.',
    culturalNotesEn: 'The Netherlands is one of the best countries in Europe for hitchhiking. Everyone speaks English. Distances are short and the country is very safe.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
    events: [
      { name: 'Koningsdag', nameEn: 'King\'s Day', date: '27 avril', dateEn: 'April 27', description: 'Tout le pays en orange, fete geante a Amsterdam, transports bondes', descriptionEn: 'Whole country in orange, huge party in Amsterdam, packed transport', type: 'holiday' },
      { name: 'Amsterdam Pride', nameEn: 'Amsterdam Pride', date: 'Aout', dateEn: 'August', description: 'Canal Parade legendaire, beaucoup de visiteurs internationaux', descriptionEn: 'Legendary Canal Parade, many international visitors', type: 'festival' },
      { name: 'Bevrijdingsdag', nameEn: 'Liberation Day', date: '5 mai', dateEn: 'May 5', description: 'Fete de la liberation, festivals gratuits dans toutes les villes', descriptionEn: 'Liberation Day, free festivals in every city', type: 'holiday' },
    ],
  },
  {
    code: 'BE',
    name: 'Belgique',
    nameEn: 'Belgium',
    nameEs: 'Bélgica',
    nameDe: 'Belgien',
    flag: '🇧🇪',
    legality: 'legal',
    legalityText: 'Légal. Reseau routier dense et travailleurs étrangers facilitent l\'autostop',
    legalityTextEn: 'Legal. Dense road network and foreign workers make hitchhiking easy',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9],
    tips: [
      'Les ronds-points pres des entrees d\'autoroute sont les meilleurs spots (Oostende, Liege, Namur)',
      'Trilingue : neerlandais en Flandre, francais en Wallonie, allemand a l\'est',
      'Adressez-vous aux conducteurs en neerlandais ("dag") ET en francais ("bonjour")',
      'Verifiez les autocollants de concessionnaires sur les voitures pour identifier la region',
      'Chaines budget : Lidl, Aldi, Colruyt. Frietkoten locaux pour moins de 5 EUR',
      'Toilettes aux services d\'autoroute : 0,30-0,70 EUR (souvent avec bon restaurant)',
      'La plupart des stations-service sont ouvertes 24h/24',
    ],
    tipsEn: [
      'Roundabouts near highway entrances are the best spots (Oostende, Liege, Namur)',
      'Trilingual: Dutch in Flanders, French in Wallonia, German in the east',
      'Address drivers in both Dutch ("dag") and French ("bonjour")',
      'Check car retailer stickers to identify owner\'s region',
      'Budget chains: Lidl, Aldi, Colruyt. Local frietkoten for meals under EUR 5',
      'Toilets at motorway services cost EUR 0.30-0.70 (often includes restaurant voucher)',
      'Most gas stations open 24/7',
    ],
    phrases: [
      { local: 'Dag / Bonjour', meaning: 'Bonjour (NL / FR)', meaningEn: 'Hello (Dutch / French)' },
      { local: 'Dank u / Merci', meaning: 'Merci (NL / FR)', meaningEn: 'Thank you (Dutch / French)' },
    ],
    culturalNotes: 'La Belgique est facile grace a son reseau routier dense et au fort trafic international. Le pays est petit donc les trajets sont rapides.',
    culturalNotesEn: 'Belgium is easy thanks to its dense road network and strong international traffic. The country is small so journeys are quick.',
    emergencyNumbers: { police: '101', ambulance: '112', fire: '112', universal: '112' },
    events: [
      { name: 'Tomorrowland', nameEn: 'Tomorrowland', date: 'Juillet', dateEn: 'July', description: 'Boom : plus grand festival electro du monde, enormement de trafic international', descriptionEn: 'Boom: world\'s biggest electronic music festival, massive international traffic', type: 'festival' },
      { name: 'Fete nationale belge', nameEn: 'Belgian National Day', date: '21 juillet', dateEn: 'July 21', description: 'Bruxelles : defile militaire et feux d\'artifice', descriptionEn: 'Brussels: military parade and fireworks', type: 'holiday' },
    ],
  },
  {
    code: 'PL',
    name: 'Pologne',
    nameEn: 'Poland',
    nameEs: 'Polonia',
    nameDe: 'Polen',
    flag: '🇵🇱',
    legality: 'legal',
    legalityText: 'Légal avec forte tradition culturelle. Organise par l\'Office du Tourisme de 1958 aux annees 1990',
    legalityTextEn: 'Legal with strong cultural tradition. Organized by Tourist Board from 1958 to 1990s',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'L\'un des pays les plus accueillants pour l\'autostop en Europe',
      'Peages (bramki) avec parking, stations MOPs sur autoroutes, bretelles d\'acces',
      'Douches gratuites aux aires de repos camions (MOPs) avec eau chaude',
      'Radio CB populaire parmi les routiers, ils relaient parfois les demandes',
      '24% parlent anglais, 20% russe, 12% allemand. Les jeunes parlent mieux',
      'Evitez les petites routes apres la tombee de la nuit',
      'Temps d\'attente 15 min a 1h, rarement plus de 2h',
    ],
    tipsEn: [
      'One of Europe\'s most hitchhiker-friendly nations',
      'Toll booths (bramki) with parking, MOP stations on highways, slip roads',
      'Free showers at truck rest stops (MOPs) with warm water',
      'CB Radio popular among truckers, they may relay requests to other drivers',
      '24% speak English, 20% Russian, 12% German. Young people speak better',
      'Avoid small roads after dark',
      'Wait times 15min to 1h, rarely over 2h',
    ],
    phrases: [
      { local: 'Dzien dobry', meaning: 'Bonjour', meaningEn: 'Good day' },
      { local: 'Dziekuje', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Jedzie pan/pani do...?', meaning: 'Allez-vous a... ?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'La Pologne a une tradition d\'autostop organisee par l\'Office National du Tourisme de 1958 aux annees 1990. Cet heritage culturel persiste et les conducteurs sont genereux envers les autostoppeurs.',
    culturalNotesEn: 'Poland has a hitchhiking tradition organized by the National Tourist Board from 1958 to 1990s. This cultural legacy persists and drivers are generous toward hitchhikers.',
    emergencyNumbers: { police: '997', ambulance: '999', fire: '998', universal: '112' },
    events: [
      { name: 'Wielka Orkiestra Swiatecznej Pomocy', nameEn: 'Great Orchestra of Christmas Charity', date: 'Janvier', dateEn: 'January', description: 'Plus grande collecte de charite au monde, tout le pays participe', descriptionEn: 'World\'s biggest charity collection, whole country participates', type: 'festival' },
      { name: 'Pol\'and\'Rock Festival', nameEn: 'Pol\'and\'Rock Festival', date: 'Aout', dateEn: 'August', description: 'Festival gratuit pres de Kostrzyn, ideal pour rencontrer des auto-stoppeurs', descriptionEn: 'Free festival near Kostrzyn, ideal for meeting fellow hitchhikers', type: 'gathering' },
    ],
  },
  {
    code: 'CZ',
    name: 'Republique Tcheque',
    nameEn: 'Czech Republic',
    nameEs: 'República Checa',
    nameDe: 'Tschechien',
    flag: '🇨🇿',
    legality: 'legal',
    legalityText: 'Légal. Pays de transit avec trafic dense, assez facile',
    legalityTextEn: 'Legal. Transit country with dense traffic, fairly easy',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 22,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Sur les routes normales, comptez environ 10 minutes. Les autoroutes demandent plus de patience',
      'L\'autostop sur autoroutes/voies rapides est interdit : utilisez les bretelles ou stations',
      'Prague est difficile, les voyageurs autoroutiers s\'y arretent souvent',
      'Les conducteurs ages parlent tcheque, parfois russe/allemand. Les jeunes parlent anglais',
      'Tous les voisins sont dans Schengen mais la police allemande controle souvent a la frontiere',
    ],
    tipsEn: [
      'On normal roads expect about 10 minutes. Highways need more patience',
      'Hitchhiking on motorways/expressways not allowed: use on-ramps or stations',
      'Prague is challenging, highway travelers often only stop there',
      'Older drivers speak Czech, sometimes Russian/German. Younger speak English',
      'All neighbors are Schengen but German police often check at Czech border',
    ],
    phrases: [
      { local: 'Dobry den', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Jedete do...?', meaning: 'Allez-vous a... ?', meaningEn: 'Are you going to...?' },
      { local: 'Dekuji', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'La Republique Tcheque est un pays de transit avec un trafic dense. L\'autostop y est assez facile, surtout en dehors de Prague.',
    culturalNotesEn: 'Czech Republic is a transit country with dense traffic. Hitchhiking is fairly easy, especially outside Prague.',
    emergencyNumbers: { police: '158', ambulance: '155', fire: '150', universal: '112' },
  },
  {
    code: 'AT',
    name: 'Autriche',
    nameEn: 'Austria',
    nameEs: 'Austria',
    nameDe: 'Österreich',
    flag: '🇦🇹',
    legality: 'legal',
    legalityText: 'Légal sauf sur les autoroutes elles-memes. Bancs d\'autostop "Mitfahrbankerl" en zones rurales',
    legalityTextEn: 'Legal except on motorways themselves. Official "Mitfahrbankerl" hitchhiking benches in rural areas',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Facile de sauter de ville en ville via les autoroutes',
      'Les stations-service desservent souvent les deux directions',
      'Bancs d\'autostop officiels "Mitfahrbankerl" en zones rurales',
      'Presque tous les Autrichiens parlent au moins un anglais de base',
      'Les zones de montagne sont plus rapides (10 min d\'attente) que les stations autoroute',
      'Paysages alpins fabuleux entre les trajets',
    ],
    tipsEn: [
      'Easy to hop between cities via motorways',
      'Service stations often serve both directions',
      'Official "Mitfahrbankerl" hitchhiking benches in rural areas',
      'Almost every Austrian speaks at least basic English',
      'Mountain areas are faster (10-min waits) vs highway stations',
      'Fabulous Alpine views between rides',
    ],
    phrases: [
      { local: 'Gruss Gott', meaning: 'Bonjour (salutation typique)', meaningEn: 'Hello (typical greeting)' },
      { local: 'Danke!', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'L\'Autriche est un bon pays pour l\'autostop avec de magnifiques vues alpines. Les Autrichiens sont polis et souvent bilingues allemand/anglais.',
    culturalNotesEn: 'Austria is a good hitchhiking country with magnificent Alpine views. Austrians are polite and often bilingual German/English.',
    emergencyNumbers: { police: '133', ambulance: '144', fire: '122', universal: '112' },
    borderCrossings: [
      'Depuis l\'Allemagne : passage de Passau (est vers Linz/Vienne) ou de Salzbourg (sud vers l\'Italie)',
    ],
    borderCrossingsEn: [
      'From Germany: Passau crossing (east toward Linz/Vienna) or Salzburg crossing (south toward Italy)',
    ],
  },
  {
    code: 'CH',
    name: 'Suisse',
    nameEn: 'Switzerland',
    nameEs: 'Suiza',
    nameDe: 'Schweiz',
    flag: '🇨🇭',
    legality: 'legal',
    legalityText: 'Interdit sur les autoroutes, mais stations-service autorisees. Bancs "Mitfahrbankli" en zones rurales',
    legalityTextEn: 'Prohibited on motorways, but gas stations allowed. "Mitfahrbankli" benches in rural areas',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Excellent dans les regions de montagne et routes rurales',
      'Quatre langues : allemand (nord/est), francais (ouest), italien (sud), romanche (est)',
      'Bancs d\'autostop officiels "Mitfahrbankli" en zones rurales',
      'Camping legal sur paturages, bois et terres agricoles non utilisees',
      '10 min d\'attente en zone germanophone, plus long a l\'ouest francophone',
      'Attention avec les camions vers l\'Italie : la police peut vous demander de partir',
    ],
    tipsEn: [
      'Excellent in mountain regions and rural roads',
      'Four languages: German (north/east), French (west), Italian (south), Romansh (east)',
      'Official "Mitfahrbankli" hitchhiking benches in rural areas',
      'Camping legal on pasture, woods and unused agricultural land',
      '10-min waits in German areas, longer in French-speaking west',
      'Caution with trucks to Italy: police may tell you to leave',
    ],
    phrases: [
      { local: 'Grüezi / Bonjour / Buongiorno', meaning: 'Bonjour (selon la region)', meaningEn: 'Hello (depending on region)' },
      { local: 'Merci vilmal / Merci', meaning: 'Merci beaucoup', meaningEn: 'Thank you very much' },
    ],
    culturalNotes: 'La Suisse est excellente pour l\'autostop en montagne. Le pays est multilingue, adaptez votre salutation a la region.',
    culturalNotesEn: 'Switzerland is excellent for mountain hitchhiking. The country is multilingual, adapt your greeting to the region.',
    emergencyNumbers: { police: '117', ambulance: '144', fire: '118', universal: '112' },
  },
  {
    code: 'PT',
    name: 'Portugal',
    nameEn: 'Portugal',
    nameEs: 'Portugal',
    nameDe: 'Portugal',
    flag: '🇵🇹',
    legality: 'legal',
    legalityText: 'L\'autostop est totalement legal. Marcher sur les autoestradas est interdit mais sans amende',
    legalityTextEn: 'Hitchhiking is completely legal. Walking on autoestradas is prohibited but no fine',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [3, 4, 5, 6, 9, 10, 11],
    tips: [
      'L\'approche directe fonctionne mieux que les panneaux en bord de route',
      'Les Portugais sont tres accueillants si vous les abordez correctement',
      'Ete tres chaud, hiver/automne avec pluies frequentes',
      'Tres venteux surtout sur la cote',
      'L\'espagnol est largement compris, bonne maitrise de l\'anglais/francais',
      'La police peut meme vous proposer un trajet si elle vous trouve sur l\'autoroute',
    ],
    tipsEn: [
      'Direct approach works better than highway signage',
      'Portuguese very friendly if approached the right way',
      'Summer extremely hot, winter/autumn frequent rainfall',
      'Very windy especially coastal areas',
      'Spanish widely understood, good English/French proficiency',
      'Police may even offer rides if they find you on the highway',
    ],
    phrases: [
      { local: 'Bom dia', meaning: 'Bonjour', meaningEn: 'Good morning' },
      { local: 'Obrigado/Obrigada', meaning: 'Merci (homme/femme)', meaningEn: 'Thank you (male/female)' },
      { local: 'Vai para...?', meaning: 'Allez-vous a... ?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'Le Portugal est assez facile pour l\'autostop. Les Portugais sont tres accueillants. L\'approche directe aux stations est plus efficace que le bord de route.',
    culturalNotesEn: 'Portugal is fairly easy for hitchhiking. Portuguese people are very welcoming. Direct approach at stations is more effective than roadside.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
    borderCrossings: [
      'Depuis l\'Espagne : AP-1/A-62 via Vilar Formoso est la route principale',
    ],
    borderCrossingsEn: [
      'From Spain: AP-1/A-62 via Vilar Formoso is the main route',
    ],
  },
  {
    code: 'IE',
    name: 'Irlande',
    nameEn: 'Ireland',
    nameEs: 'Irlanda',
    nameDe: 'Irland',
    flag: '🇮🇪',
    legality: 'legal',
    legalityText: 'Excellent pays pour l\'autostop. Legal de se tenir sur les routes a chaussees separees',
    legalityTextEn: 'Excellent hitchhiking country. Legal to stand on dual carriageways',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very Easy',
    avgWaitTime: 12,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Meme ceux qui ne vous prennent pas agitent la main, klaxonnent ou levent le pouce',
      'Les ronds-points aux entrees/sorties d\'autoroute : moins de 5 min d\'attente',
      'Le pouce est le standard, les panneaux en carton reduisent significativement l\'attente',
      'Plusieurs courts trajets battent l\'attente d\'un long trajet',
      'Camping dans les champs vides : les fermiers ne s\'y opposent generalement pas si vous demandez',
      'Preparez-vous a de longues et amicales conversations avec les conducteurs',
    ],
    tipsEn: [
      'Even those who don\'t pick you up wave, honk or show thumbs up',
      'Roundabouts at entry/exit ramps: under 5min wait',
      'Thumb is standard, cardboard signs significantly reduce waiting',
      'Multiple short rides beat waiting for one long ride',
      'Camping in empty fields: farmers generally don\'t object if you ask',
      'Prepare for long and friendly conversations with drivers',
    ],
    phrases: [
      { local: 'How\'s it going?', meaning: 'Ca va ? (salutation typique)', meaningEn: 'Typical Irish greeting' },
      { local: 'Cheers!', meaning: 'Merci (informel)', meaningEn: 'Thanks (informal)' },
    ],
    culturalNotes: 'L\'Irlande est l\'un des meilleurs pays au monde pour l\'autostop. Les conducteurs sont communicatifs et accueillants. Meme sans vous prendre, beaucoup saluent amicalement.',
    culturalNotesEn: 'Ireland is one of the best countries in the world for hitchhiking. Drivers are communicative and welcoming. Even without picking you up, many greet you warmly.',
    emergencyNumbers: { police: '999', ambulance: '999', fire: '999', universal: '112' },
  },
  {
    code: 'GB',
    name: 'Royaume-Uni',
    nameEn: 'United Kingdom',
    nameEs: 'Reino Unido',
    nameDe: 'Vereinigtes Königreich',
    flag: '🇬🇧',
    legality: 'legal',
    legalityText: 'Légal mais en declin. Interdit de marcher sur les autoroutes',
    legalityTextEn: 'Legal but declining. Illegal to walk on motorways',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'L\'Ecosse et le Pays de Galles sont plus faciles que le sud de l\'Angleterre',
      'Les meilleurs spots : bas des bretelles d\'acces et stations-service d\'autoroute',
      'Utilisez les noms d\'autoroute sur les panneaux (M4, M1) plutot que les noms de villes',
      'Parlez aux conducteurs qui font le plein, montrez panneaux ou cartes',
      '"Rides" = "lifts", "trucks" = "lorries" en anglais britannique',
      'Meteo pluvieuse notoire, preparez des vetements impermeable',
    ],
    tipsEn: [
      'Scotland and Wales easier than southern England',
      'Best spots: slip road bottoms and motorway service stations',
      'Use motorway names on signs (M4, M1) rather than city names',
      'Talk with drivers refueling, display signs or maps',
      'UK English: "rides" = "lifts", "trucks" = "lorries"',
      'Infamous rainy weather, prepare waterproofs',
    ],
    phrases: [
      { local: 'Could I get a lift?', meaning: 'Puis-je avoir un trajet ?', meaningEn: 'Can I get a ride?' },
      { local: 'Cheers, mate!', meaning: 'Merci, l\'ami !', meaningEn: 'Thanks, buddy!' },
    ],
    culturalNotes: 'L\'autostop au Royaume-Uni est possible mais en declin de popularite. L\'Ecosse et le Pays de Galles restent plus accueillants que l\'Angleterre.',
    culturalNotesEn: 'Hitchhiking in the UK is possible but declining in popularity. Scotland and Wales remain more welcoming than England.',
    emergencyNumbers: { police: '999', ambulance: '999', fire: '999', universal: '112' },
    borderCrossings: [
      'Traversee vers la France depuis Dover : les camionneurs par ferry (pas Eurotunnel) peuvent prendre des passagers',
      'Repas gratuits parfois offerts sur le ferry',
    ],
    borderCrossingsEn: [
      'Crossing to France from Dover: truckers via ferry (not Eurotunnel) can take passengers',
      'Free meals sometimes offered on the ferry',
    ],
  },
  {
    code: 'SE',
    name: 'Suede',
    nameEn: 'Sweden',
    nameEs: 'Suecia',
    nameDe: 'Schweden',
    flag: '🇸🇪',
    legality: 'legal',
    legalityText: 'Légal partout sauf sur les autoroutes elles-memes',
    legalityTextEn: 'Legal everywhere except on motorways themselves',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 35,
    bestMonths: [5, 6, 7, 8],
    tips: [
      'Longues distances entre les villes, preparez-vous',
      'Les Suedois sont reserves mais serviables une fois que vous les abordez',
      'Les stations-service sont les meilleurs spots',
      'L\'anglais est tres bien parle dans tout le pays',
      'En ete les jours sont tres longs, profitez-en pour voyager',
    ],
    tipsEn: [
      'Long distances between cities, be prepared',
      'Swedes are reserved but helpful once you approach them',
      'Gas stations are the best spots',
      'English is very well spoken throughout the country',
      'In summer days are very long, take advantage for traveling',
    ],
    culturalNotes: 'La Suede est un bon pays mais les distances sont longues. Les gens sont reserves mais gentils.',
    culturalNotesEn: 'Sweden is a good country but distances are long. People are reserved but kind.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'NO',
    name: 'Norvege',
    nameEn: 'Norway',
    nameEs: 'Noruega',
    nameDe: 'Norwegen',
    flag: '🇳🇴',
    legality: 'legal',
    legalityText: 'Légal. Beau mais éloigné, peu de voitures hors des axes principaux',
    legalityTextEn: 'Legal. Beautiful but remote, few cars outside main routes',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 40,
    bestMonths: [6, 7, 8],
    tips: [
      'Magnifiques paysages mais peu de circulation hors des routes principales',
      'Les stations-service et les aires de ferry sont les meilleurs endroits',
      'L\'anglais est tres bien parle',
      'Preparez-vous au froid meme en ete dans le nord',
      'Les ferries sont frequents et offrent des opportunites de rencontrer des conducteurs',
    ],
    tipsEn: [
      'Beautiful landscapes but little traffic outside main roads',
      'Gas stations and ferry terminals are the best spots',
      'English is very well spoken',
      'Prepare for cold even in summer in the north',
      'Ferries are frequent and offer opportunities to meet drivers',
    ],
    culturalNotes: 'La Norvege est magnifique mais eloignee. Peu de voitures en dehors des axes principaux. Les ferries sont un bon moyen de rencontrer des conducteurs.',
    culturalNotesEn: 'Norway is beautiful but remote. Few cars outside main routes. Ferries are a good way to meet drivers.',
    emergencyNumbers: { police: '112', ambulance: '113', fire: '110', universal: '112' },
  },
  {
    code: 'DK',
    name: 'Danemark',
    nameEn: 'Denmark',
    nameEs: 'Dinamarca',
    nameDe: 'Dänemark',
    flag: '🇩🇰',
    legality: 'legal',
    legalityText: 'Légal et facile. Petit pays avec des gens accueillants',
    legalityTextEn: 'Legal and easy. Small country with welcoming people',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Petit pays, distances courtes entre les villes',
      'Les Danois sont accueillants et parlent tres bien anglais',
      'Les stations-service et ronds-points sont les meilleurs spots',
      'La traversee vers la Suede par le pont de l\'Oresund peut se faire en stop',
    ],
    tipsEn: [
      'Small country, short distances between cities',
      'Danes are welcoming and speak English very well',
      'Gas stations and roundabouts are the best spots',
      'The crossing to Sweden via the Oresund Bridge can be done by hitching',
    ],
    culturalNotes: 'Le Danemark est facile et accueillant. Petit pays avec des distances courtes.',
    culturalNotesEn: 'Denmark is easy and welcoming. Small country with short distances.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'FI',
    name: 'Finlande',
    nameEn: 'Finland',
    nameEs: 'Finlandia',
    nameDe: 'Finnland',
    flag: '🇫🇮',
    legality: 'legal',
    legalityText: 'Légal mais difficile a cause des longues distances et du trafic clairseme',
    legalityTextEn: 'Legal but difficult due to long distances and sparse traffic',
    difficulty: 4,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Difficult',
    avgWaitTime: 45,
    bestMonths: [6, 7, 8],
    tips: [
      'Longues distances et trafic clairseme, surtout dans le nord',
      'Les stations-service sont essentielles comme points de depart',
      'L\'anglais est bien parle, surtout par les jeunes',
      'En ete, le soleil de minuit permet de voyager tres tard',
      'Preparez des vetements chauds meme en ete pour le nord',
    ],
    tipsEn: [
      'Long distances and sparse traffic, especially in the north',
      'Gas stations are essential as starting points',
      'English is well spoken, especially by younger people',
      'In summer, midnight sun allows traveling very late',
      'Prepare warm clothes even in summer for the north',
    ],
    culturalNotes: 'La Finlande est un defi a cause des longues distances et du trafic clairseme. Le nord est particulierement difficile.',
    culturalNotesEn: 'Finland is challenging due to long distances and sparse traffic. The north is particularly difficult.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'HU',
    name: 'Hongrie',
    nameEn: 'Hungary',
    nameEs: 'Hungría',
    nameDe: 'Ungarn',
    flag: '🇭🇺',
    legality: 'legal',
    legalityText: 'Légal avec bonne tradition d\'autostop',
    legalityTextEn: 'Legal with good hitchhiking tradition',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Bonne tradition d\'autostop dans le pays',
      'Les stations MOL sont repandues et fonctionnent bien',
      'Beaucoup de trafic de transit est-ouest',
      'Le hongrois est difficile mais les jeunes parlent anglais',
      'Budapest peut etre difficile a quitter, utilisez les stations en peripherie',
    ],
    tipsEn: [
      'Good hitchhiking tradition in the country',
      'MOL gas stations are widespread and work well',
      'Lots of east-west transit traffic',
      'Hungarian is difficult but young people speak English',
      'Budapest can be hard to leave, use stations on outskirts',
    ],
    phrases: [
      { local: 'Jo napot', meaning: 'Bonjour', meaningEn: 'Good day' },
      { local: 'Koszonom', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'La Hongrie a une bonne tradition d\'autostop. Le trafic de transit est-ouest est un avantage.',
    culturalNotesEn: 'Hungary has a good hitchhiking tradition. East-west transit traffic is an advantage.',
    emergencyNumbers: { police: '107', ambulance: '104', fire: '105', universal: '112' },
  },
  {
    code: 'HR',
    name: 'Croatie',
    nameEn: 'Croatia',
    nameEs: 'Croacia',
    nameDe: 'Kroatien',
    flag: '🇭🇷',
    legality: 'legal',
    legalityText: 'Légal et facile surtout en ete avec le trafic touristique',
    legalityTextEn: 'Legal and easy especially in summer with tourist traffic',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Tres facile en ete grace au trafic touristique',
      'Les stations-service INA sont les plus courantes',
      'Le littoral est plus facile que l\'interieur',
      'Beaucoup de conducteurs parlent anglais ou allemand',
      'Les peages sur l\'A1 Zagreb-Split sont de bons spots',
    ],
    tipsEn: [
      'Very easy in summer thanks to tourist traffic',
      'INA gas stations are the most common',
      'Coastline is easier than inland',
      'Many drivers speak English or German',
      'Toll barriers on the A1 Zagreb-Split are good spots',
    ],
    culturalNotes: 'La Croatie est facile en ete grace au trafic touristique intense sur la cote. L\'interieur est plus calme.',
    culturalNotesEn: 'Croatia is easy in summer thanks to intense tourist traffic on the coast. Inland is quieter.',
    emergencyNumbers: { police: '192', ambulance: '194', fire: '193', universal: '112' },
  },
  {
    code: 'RO',
    name: 'Roumanie',
    nameEn: 'Romania',
    nameEs: 'Rumanía',
    nameDe: 'Rumänien',
    flag: '🇷🇴',
    legality: 'legal',
    legalityText: 'Légal et très commun. Les Roumains sont très accueillants',
    legalityTextEn: 'Legal and very common. Romanians are very welcoming',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'L\'autostop est tres commun et les Roumains tres accueillants',
      'Les stations Petrom et OMV sont repandues',
      'Beaucoup de conducteurs proposent spontanement des trajets',
      'Les jeunes parlent anglais, les plus ages parfois francais',
      'Les routes nationales sont souvent plus pratiques que les autoroutes (peu nombreuses)',
    ],
    tipsEn: [
      'Hitchhiking is very common and Romanians very welcoming',
      'Petrom and OMV stations are widespread',
      'Many drivers spontaneously offer rides',
      'Young people speak English, older sometimes French',
      'National roads are often more practical than motorways (few exist)',
    ],
    phrases: [
      { local: 'Buna ziua', meaning: 'Bonjour', meaningEn: 'Good day' },
      { local: 'Multumesc', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'La Roumanie est tres accueillante pour les autostoppeurs. L\'autostop y est commun et les conducteurs genereux.',
    culturalNotesEn: 'Romania is very welcoming for hitchhikers. Hitchhiking is common and drivers are generous.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'GR',
    name: 'Grece',
    nameEn: 'Greece',
    nameEs: 'Grecia',
    nameDe: 'Griechenland',
    flag: '🇬🇷',
    legality: 'legal',
    legalityText: 'Légal mais peu commun. Possible avec patience',
    legalityTextEn: 'Legal but uncommon. Possible with patience',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 35,
    bestMonths: [4, 5, 6, 9, 10],
    tips: [
      'L\'autostop n\'est pas commun mais les Grecs sont genereux',
      'Les stations-service et les peages sont les meilleurs endroits',
      'Evitez les heures les plus chaudes en ete',
      'L\'anglais est bien parle dans les zones touristiques',
      'Les iles sont difficiles, le continent est plus facile',
    ],
    tipsEn: [
      'Hitchhiking is not common but Greeks are generous',
      'Gas stations and toll barriers are the best spots',
      'Avoid the hottest hours in summer',
      'English is well spoken in tourist areas',
      'Islands are difficult, mainland is easier',
    ],
    phrases: [
      { local: 'Kalimera', meaning: 'Bonjour', meaningEn: 'Good morning' },
      { local: 'Efcharisto', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'La Grece n\'a pas de forte tradition d\'autostop mais les Grecs sont genereux et hospitaliers une fois aborde.',
    culturalNotesEn: 'Greece doesn\'t have a strong hitchhiking tradition but Greeks are generous and hospitable once approached.',
    emergencyNumbers: { police: '100', ambulance: '166', fire: '199', universal: '112' },
  },
  {
    code: 'BG',
    name: 'Bulgarie',
    nameEn: 'Bulgaria',
    nameEs: 'Bulgaria',
    nameDe: 'Bulgarien',
    flag: '🇧🇬',
    legality: 'legal',
    legalityText: 'Légal et facile. Les locaux sont serviables',
    legalityTextEn: 'Legal and easy. Locals are helpful',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Les Bulgares sont accueillants et serviables',
      'Les stations-service sur les routes nationales sont de bons spots',
      'Attention : hocher la tete signifie "non" et secouer la tete signifie "oui"',
      'Les jeunes parlent anglais, les plus ages parfois russe',
      'Le trafic de transit vers la Turquie/Grece est un avantage',
    ],
    tipsEn: [
      'Bulgarians are welcoming and helpful',
      'Gas stations on national roads are good spots',
      'Warning: nodding means "no" and shaking head means "yes"',
      'Young people speak English, older sometimes Russian',
      'Transit traffic to Turkey/Greece is an advantage',
    ],
    phrases: [
      { local: 'Zdraveyte', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Blagodarya', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'La Bulgarie est facile pour l\'autostop. Attention au langage corporel inverse (hochement = non).',
    culturalNotesEn: 'Bulgaria is easy for hitchhiking. Watch out for reversed body language (nodding = no).',
    emergencyNumbers: { police: '166', ambulance: '150', fire: '160', universal: '112' },
  },
  {
    code: 'SK',
    name: 'Slovaquie',
    nameEn: 'Slovakia',
    nameEs: 'Eslovaquia',
    nameDe: 'Slowakei',
    flag: '🇸🇰',
    legality: 'legal',
    legalityText: 'Légal et facile grace au trafic de transit',
    legalityTextEn: 'Legal and easy thanks to transit traffic',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Pays de transit avec bon trafic est-ouest',
      'Les stations-service sur la D1 sont de bons spots',
      'Les jeunes parlent anglais, les plus ages tcheque ou hongrois',
      'Bratislava est bien connectee a Vienne (60 km)',
      'Les Slovaques sont accueillants et curieux',
    ],
    tipsEn: [
      'Transit country with good east-west traffic',
      'Gas stations on the D1 are good spots',
      'Young people speak English, older Czech or Hungarian',
      'Bratislava is well connected to Vienna (60km)',
      'Slovaks are welcoming and curious',
    ],
    phrases: [
      { local: 'Dobry den', meaning: 'Bonjour', meaningEn: 'Good day' },
      { local: 'Dakujem', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'La Slovaquie est un pays de transit facile. Les distances sont courtes et les conducteurs accueillants.',
    culturalNotesEn: 'Slovakia is an easy transit country. Distances are short and drivers welcoming.',
    emergencyNumbers: { police: '158', ambulance: '155', fire: '150', universal: '112' },
  },
  {
    code: 'SI',
    name: 'Slovenie',
    nameEn: 'Slovenia',
    nameEs: 'Eslovenia',
    nameDe: 'Slowenien',
    flag: '🇸🇮',
    legality: 'legal',
    legalityText: 'Légal et facile. Petit pays avec bonnes connexions',
    legalityTextEn: 'Legal and easy. Small country with good connections',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Petit pays, les distances sont courtes',
      'Ljubljana est bien connectee dans toutes les directions',
      'Beaucoup de trafic de transit Italie-Autriche et Autriche-Croatie',
      'L\'anglais est bien parle, surtout par les jeunes',
      'Les stations Petrol sont les plus repandues',
    ],
    tipsEn: [
      'Small country, distances are short',
      'Ljubljana is well connected in all directions',
      'Lots of transit traffic Italy-Austria and Austria-Croatia',
      'English is well spoken, especially by young people',
      'Petrol stations are the most common',
    ],
    culturalNotes: 'La Slovenie est un petit pays facile a traverser. Le trafic de transit international est un avantage.',
    culturalNotesEn: 'Slovenia is a small country easy to cross. International transit traffic is an advantage.',
    emergencyNumbers: { police: '113', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'IS',
    name: 'Islande',
    nameEn: 'Iceland',
    nameEs: 'Islandia',
    nameDe: 'Island',
    flag: '🇮🇸',
    legality: 'legal',
    legalityText: 'Légal. Difficile en raison du trafic très faible hors route 1',
    legalityTextEn: 'Legal. Difficult due to very low traffic outside Route 1',
    difficulty: 4,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Difficult',
    avgWaitTime: 60,
    bestMonths: [6, 7, 8],
    tips: [
      'La Route 1 (route circulaire) est la seule voie majeure',
      'En ete, les touristes en voiture de location s\'arretent souvent',
      'Le trafic est tres faible en dehors de Reykjavik',
      'Preparez-vous au vent et au froid meme en ete',
      'Soleil de minuit en ete : voyagez a toute heure',
    ],
    tipsEn: [
      'Route 1 (ring road) is the only major road',
      'In summer, tourists in rental cars often stop',
      'Traffic is very low outside Reykjavik',
      'Prepare for wind and cold even in summer',
      'Midnight sun in summer: travel at any hour',
    ],
    culturalNotes: 'L\'Islande est un defi en raison du faible trafic mais le soleil de minuit en ete permet de voyager a toute heure.',
    culturalNotesEn: 'Iceland is challenging due to low traffic but midnight sun in summer allows traveling at any hour.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'BA',
    name: 'Bosnie-Herzegovine',
    nameEn: 'Bosnia and Herzegovina',
    nameEs: 'Bosnia y Herzegovina',
    nameDe: 'Bosnien und Herzegowina',
    flag: '🇧🇦',
    legality: 'legal',
    legalityText: 'Légal et facile. Les Bosniens sont très accueillants',
    legalityTextEn: 'Legal and easy. Bosnians are very welcoming',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Les Bosniens sont tres accueillants et curieux des voyageurs',
      'Le cafe bosniaque (bosanska kava) sera souvent offert',
      'Les stations-service Petrol et Hifa-Petrol sont les plus repandues',
      'Beaucoup de trafic de transit entre Zagreb et Belgrade',
      'Les jeunes parlent anglais, les plus ages parfois allemand ou italien',
      'Evitez les zones marquees par des panneaux de mines non desamorcees',
    ],
    tipsEn: [
      'Bosnians are very welcoming and curious about travelers',
      'Bosnian coffee (bosanska kava) is often offered',
      'Petrol and Hifa-Petrol gas stations are most common',
      'Lots of transit traffic between Zagreb and Belgrade',
      'Young people speak English, older sometimes German or Italian',
      'Avoid areas marked with unexploded mine warnings',
    ],
    phrases: [
      { local: 'Zdravo', meaning: 'Salut', meaningEn: 'Hi' },
      { local: 'Hvala', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Idete li u pravcu...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going toward...?' },
    ],
    culturalNotes: 'La Bosnie-Herzegovine est facile pour l\'autostop. Les Bosniens sont tres accueillants malgre un passe difficile. Le cafe et la conversation font partie de la culture.',
    culturalNotesEn: 'Bosnia and Herzegovina is easy for hitchhiking. Bosnians are very welcoming despite a difficult past. Coffee and conversation are part of the culture.',
    emergencyNumbers: { police: '122', ambulance: '124', fire: '123', universal: '112' },
  },
  {
    code: 'BY',
    name: 'Biélorussie',
    nameEn: 'Belarus',
    nameEs: 'Bielorrusia',
    nameDe: 'Belarus',
    flag: '🇧🇾',
    legality: 'legal',
    legalityText: 'Légal. Pays de transit est-ouest avec bon trafic. Visa requis pour la plupart des nationalites',
    legalityTextEn: 'Legal. East-west transit country with good traffic. Visa required for most nationalities',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Visa requis pour la plupart des voyageurs (exception : transit 5 jours par Minsk)',
      'Le russe est essentiel, peu de gens parlent anglais',
      'Les stations-service Belorusneft sont repandues',
      'Le trafic de transit Pologne-Russie est important sur la M1',
      'Les policiers peuvent controler vos documents frequemment',
      'Les routes sont en bon etat, hiver tres rigoureux',
    ],
    tipsEn: [
      'Visa required for most travelers (exception: 5-day transit via Minsk)',
      'Russian is essential, few people speak English',
      'Belorusneft gas stations are widespread',
      'Poland-Russia transit traffic is heavy on M1',
      'Police may check your documents frequently',
      'Roads are in good condition, winter is very harsh',
    ],
    phrases: [
      { local: 'Zdravstvuyte', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Spasibo', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Vy yedete v...?', meaning: 'Allez-vous a...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'La Bielorussie est un pays de transit avec un bon trafic. Visa requis. Le russe est indispensable. Les Bielorusses sont reserves mais serviables.',
    culturalNotesEn: 'Belarus is a transit country with good traffic. Visa required. Russian is essential. Belarusians are reserved but helpful.',
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '112' },
  },
  {
    code: 'EE',
    name: 'Estonie',
    nameEn: 'Estonia',
    nameEs: 'Estonia',
    nameDe: 'Estland',
    flag: '🇪🇪',
    legality: 'legal',
    legalityText: 'Légal et assez facile. Petit pays bien connecte',
    legalityTextEn: 'Legal and fairly easy. Small well-connected country',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Petit pays, distances courtes entre les villes',
      'Les Estoniens sont reserves mais parlent tres bien anglais',
      'Les stations-service Alexela et Circle K sont repandues',
      'Le trafic vers/depuis la Finlande (ferry Tallinn-Helsinki) est important',
      'Routes en excellent etat, infrastructure moderne',
      'Tallinn peut etre difficile a quitter, utilisez les peripheriques',
    ],
    tipsEn: [
      'Small country, short distances between cities',
      'Estonians are reserved but speak English very well',
      'Alexela and Circle K gas stations are widespread',
      'Traffic to/from Finland (Tallinn-Helsinki ferry) is heavy',
      'Roads in excellent condition, modern infrastructure',
      'Tallinn can be hard to leave, use ring roads',
    ],
    phrases: [
      { local: 'Tere', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Aitah', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Kas te laete...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'L\'Estonie est un petit pays facile pour l\'autostop. Les Estoniens sont reserves mais parlent tres bien anglais. Infrastructure moderne.',
    culturalNotesEn: 'Estonia is a small country easy for hitchhiking. Estonians are reserved but speak English very well. Modern infrastructure.',
    emergencyNumbers: { police: '110', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'LT',
    name: 'Lituanie',
    nameEn: 'Lithuania',
    nameEs: 'Lituania',
    nameDe: 'Litauen',
    flag: '🇱🇹',
    legality: 'legal',
    legalityText: 'Légal et facile. Bonne tradition d\'autostop',
    legalityTextEn: 'Legal and easy. Good hitchhiking tradition',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Les Lituaniens sont accueillants et l\'autostop est commun',
      'Les stations Circle K et Viada sont tres repandues',
      'Beaucoup de trafic de transit vers la Pologne et la Lettonie',
      'Les jeunes parlent bien anglais, les plus ages parfois russe',
      'Routes en bon etat, surtout sur la Via Baltica (A1)',
      'Vilnius est bien connectee dans toutes les directions',
    ],
    tipsEn: [
      'Lithuanians are welcoming and hitchhiking is common',
      'Circle K and Viada stations are very widespread',
      'Lots of transit traffic to Poland and Latvia',
      'Young people speak English well, older sometimes Russian',
      'Roads in good condition, especially on Via Baltica (A1)',
      'Vilnius is well connected in all directions',
    ],
    phrases: [
      { local: 'Labas', meaning: 'Salut', meaningEn: 'Hi' },
      { local: 'Aciu', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Ar jus vazuojate i...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'La Lituanie est facile pour l\'autostop avec une bonne tradition. Les Lituaniens sont accueillants et le trafic de transit est un avantage.',
    culturalNotesEn: 'Lithuania is easy for hitchhiking with a good tradition. Lithuanians are welcoming and transit traffic is an advantage.',
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'LU',
    name: 'Luxembourg',
    nameEn: 'Luxembourg',
    nameEs: 'Luxemburgo',
    nameDe: 'Luxemburg',
    flag: '🇱🇺',
    legality: 'legal',
    legalityText: 'Légal. Tres petit pays avec excellent trafic de transit',
    legalityTextEn: 'Legal. Very small country with excellent transit traffic',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 15,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Tres petit pays, on le traverse en moins d\'une heure',
      'Trilingue : luxembourgeois, francais, allemand. Anglais largement parle',
      'Enorme trafic de transit entre Belgique, France et Allemagne',
      'Les stations-service sont nombreuses et efficaces',
      'Ronds-points aux entrees d\'autoroute : attente de 5-15 minutes',
      'Le Luxembourg est tres sur et les conducteurs sont polis',
    ],
    tipsEn: [
      'Very small country, can cross it in under an hour',
      'Trilingual: Luxembourgish, French, German. English widely spoken',
      'Huge transit traffic between Belgium, France and Germany',
      'Gas stations are numerous and efficient',
      'Roundabouts at highway entrances: 5-15 minute waits',
      'Luxembourg is very safe and drivers are polite',
    ],
    phrases: [
      { local: 'Moien', meaning: 'Bonjour (luxembourgeois)', meaningEn: 'Hello (Luxembourgish)' },
      { local: 'Merci / Danke', meaning: 'Merci (FR/DE)', meaningEn: 'Thank you (FR/DE)' },
    ],
    culturalNotes: 'Le Luxembourg est tres facile grace a sa petite taille et son enorme trafic de transit. Tres sur et multilingue.',
    culturalNotesEn: 'Luxembourg is very easy thanks to its small size and huge transit traffic. Very safe and multilingual.',
    emergencyNumbers: { police: '113', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'LV',
    name: 'Lettonie',
    nameEn: 'Latvia',
    nameEs: 'Letonia',
    nameDe: 'Lettland',
    flag: '🇱🇻',
    legality: 'legal',
    legalityText: 'Légal et facile. Bon trafic de transit entre les pays baltes',
    legalityTextEn: 'Legal and easy. Good transit traffic between Baltic countries',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Les Lettons sont reserves mais serviables',
      'Les stations Circle K et Neste sont repandues',
      'Le trafic Via Baltica (A1) est excellent pour les longues distances',
      'Les jeunes parlent bien anglais, les plus ages russe',
      'Riga peut etre difficile a quitter, utilisez les peripheriques',
      'Les routes sont en bon etat, bien entretenues',
    ],
    tipsEn: [
      'Latvians are reserved but helpful',
      'Circle K and Neste stations are widespread',
      'Via Baltica (A1) traffic is excellent for long distances',
      'Young people speak English well, older Russian',
      'Riga can be hard to leave, use ring roads',
      'Roads are in good condition, well maintained',
    ],
    phrases: [
      { local: 'Sveiki', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Paldies', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Vai jus braucat uz...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'La Lettonie est facile pour l\'autostop. Les Lettons sont reserves mais le trafic de transit est bon. Infrastructure moderne.',
    culturalNotesEn: 'Latvia is easy for hitchhiking. Latvians are reserved but transit traffic is good. Modern infrastructure.',
    emergencyNumbers: { police: '110', ambulance: '113', fire: '112', universal: '112' },
  },
  {
    code: 'MD',
    name: 'Moldavie',
    nameEn: 'Moldova',
    nameEs: 'Moldavia',
    nameDe: 'Moldau',
    flag: '🇲🇩',
    legality: 'legal',
    legalityText: 'Légal et commun. Les Moldaves sont très accueillants',
    legalityTextEn: 'Legal and common. Moldovans are very welcoming',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Les Moldaves sont tres accueillants et genereux',
      'Le russe et le roumain sont parles. Peu d\'anglais hors de Chisinau',
      'Les stations-service Bemol et Lukoil sont les plus repandues',
      'Le trafic vers la Roumanie et l\'Ukraine est important',
      'On vous offrira souvent du vin moldave (tradition viticole)',
      'Routes en etat variable, attention aux nids-de-poule',
    ],
    tipsEn: [
      'Moldovans are very welcoming and generous',
      'Russian and Romanian are spoken. Little English outside Chisinau',
      'Bemol and Lukoil gas stations are most common',
      'Traffic to Romania and Ukraine is heavy',
      'Moldovan wine is often offered (winemaking tradition)',
      'Roads in variable condition, watch for potholes',
    ],
    phrases: [
      { local: 'Buna ziua', meaning: 'Bonjour (roumain)', meaningEn: 'Hello (Romanian)' },
      { local: 'Multumesc', meaning: 'Merci (roumain)', meaningEn: 'Thank you (Romanian)' },
      { local: 'Spasibo', meaning: 'Merci (russe)', meaningEn: 'Thank you (Russian)' },
    ],
    culturalNotes: 'La Moldavie est facile et tres accueillante. Les Moldaves sont genereux. Le vin est une tradition. Routes en etat variable.',
    culturalNotesEn: 'Moldova is easy and very welcoming. Moldovans are generous. Wine is a tradition. Roads in variable condition.',
    emergencyNumbers: { police: '902', ambulance: '903', fire: '901', universal: '112' },
  },
  {
    code: 'ME',
    name: 'Montenegro',
    nameEn: 'Montenegro',
    nameEs: 'Montenegro',
    nameDe: 'Montenegro',
    flag: '🇲🇪',
    legality: 'legal',
    legalityText: 'Légal et facile. Petit pays montagneux avec bons conducteurs',
    legalityTextEn: 'Legal and easy. Small mountainous country with good drivers',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Tres petit pays, distances courtes mais routes sinueuses en montagne',
      'Les Montenegrins sont accueillants et curieux',
      'Les stations-service Petrol et Jugopetrol sont repandues',
      'Le trafic touristique vers la cote (Kotor, Budva) est tres important en ete',
      'Beaucoup de conducteurs parlent anglais dans les zones touristiques',
      'Les paysages sont spectaculaires mais les routes etroites',
    ],
    tipsEn: [
      'Very small country, short distances but winding mountain roads',
      'Montenegrins are welcoming and curious',
      'Petrol and Jugopetrol gas stations are widespread',
      'Tourist traffic to the coast (Kotor, Budva) is very heavy in summer',
      'Many drivers speak English in tourist areas',
      'Landscapes are spectacular but roads are narrow',
    ],
    phrases: [
      { local: 'Zdravo', meaning: 'Salut', meaningEn: 'Hi' },
      { local: 'Hvala', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Idete li prema...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going toward...?' },
    ],
    culturalNotes: 'Le Montenegro est facile et accueillant. Petit pays montagneux avec des paysages spectaculaires. Le trafic touristique est un avantage en ete.',
    culturalNotesEn: 'Montenegro is easy and welcoming. Small mountainous country with spectacular landscapes. Tourist traffic is an advantage in summer.',
    emergencyNumbers: { police: '122', ambulance: '124', fire: '123', universal: '112' },
  },
  {
    code: 'MK',
    name: 'Macedoine du Nord',
    nameEn: 'North Macedonia',
    nameEs: 'Macedonia del Norte',
    nameDe: 'Nordmazedonien',
    flag: '🇲🇰',
    legality: 'legal',
    legalityText: 'Légal et assez facile. Pays de transit important',
    legalityTextEn: 'Legal and fairly easy. Important transit country',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 25,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Pays de transit important entre Grece, Bulgarie et Serbie',
      'Les Macedoniens sont accueillants et curieux',
      'Les stations-service Makpetrol et Lukoil sont repandues',
      'Les jeunes parlent anglais, les plus ages parfois grec ou serbe',
      'Routes en etat variable, autoroutes modernes sur les axes principaux',
      'Skopje est bien connectee dans toutes les directions',
    ],
    tipsEn: [
      'Important transit country between Greece, Bulgaria and Serbia',
      'Macedonians are welcoming and curious',
      'Makpetrol and Lukoil gas stations are widespread',
      'Young people speak English, older sometimes Greek or Serbian',
      'Roads in variable condition, modern highways on main routes',
      'Skopje is well connected in all directions',
    ],
    phrases: [
      { local: 'Zdravo', meaning: 'Salut', meaningEn: 'Hi' },
      { local: 'Blagodaram', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Odate li kon...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going toward...?' },
    ],
    culturalNotes: 'La Macedoine du Nord est facile pour l\'autostop. Pays de transit important. Les Macedoniens sont accueillants.',
    culturalNotesEn: 'North Macedonia is easy for hitchhiking. Important transit country. Macedonians are welcoming.',
    emergencyNumbers: { police: '192', ambulance: '194', fire: '193', universal: '112' },
  },
  {
    code: 'RS',
    name: 'Serbie',
    nameEn: 'Serbia',
    nameEs: 'Serbia',
    nameDe: 'Serbien',
    flag: '🇷🇸',
    legality: 'legal',
    legalityText: 'Légal et facile. Les Serbes sont très accueillants',
    legalityTextEn: 'Legal and easy. Serbians are very welcoming',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'Les Serbes sont parmi les plus accueillants d\'Europe',
      'Les stations-service NIS et Gazprom sont tres repandues',
      'Le trafic de transit Budapest-Sofia et Zagreb-Istanbul est important',
      'Les jeunes parlent bien anglais, les plus ages parfois russe',
      'Belgrade peut etre difficile a quitter, utilisez les peripheriques',
      'On vous offrira souvent de la rakija (eau-de-vie locale)',
    ],
    tipsEn: [
      'Serbians are among the most welcoming in Europe',
      'NIS and Gazprom gas stations are very widespread',
      'Transit traffic Budapest-Sofia and Zagreb-Istanbul is heavy',
      'Young people speak English well, older sometimes Russian',
      'Belgrade can be hard to leave, use ring roads',
      'Rakija (local brandy) is often offered',
    ],
    phrases: [
      { local: 'Zdravo', meaning: 'Salut', meaningEn: 'Hi' },
      { local: 'Hvala', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Da li idete prema...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going toward...?' },
    ],
    culturalNotes: 'La Serbie est facile et tres accueillante. Les Serbes sont chaleureux et genereux. Le trafic de transit est un avantage.',
    culturalNotesEn: 'Serbia is easy and very welcoming. Serbians are warm and generous. Transit traffic is an advantage.',
    emergencyNumbers: { police: '192', ambulance: '194', fire: '193', universal: '112' },
  },
  {
    code: 'UA',
    name: 'Ukraine',
    nameEn: 'Ukraine',
    nameEs: 'Ucrania',
    nameDe: 'Ukraine',
    flag: '🇺🇦',
    legality: 'legal',
    legalityText: 'Légal et très commun. Attention a la situation actuelle (guerre depuis 2022)',
    legalityTextEn: 'Legal and very common. Warning: current situation (war since 2022)',
    difficulty: 3,
    difficultyText: 'Moyen (situation speciale)',
    difficultyTextEn: 'Medium (special situation)',
    avgWaitTime: 25,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'AVERTISSEMENT : Guerre en cours depuis 2022. Zones de conflit a l\'est et au sud',
      'L\'autostop est tres commun et culturellement accepte',
      'Les Ukrainiens sont extremement accueillants et genereux',
      'Le russe et l\'ukrainien sont parles. Anglais limite hors de Kiev',
      'Les stations-service WOG et OKKO sont repandues',
      'Routes en etat variable, certaines endommagees par la guerre',
    ],
    tipsEn: [
      'WARNING: Ongoing war since 2022. Conflict zones in east and south',
      'Hitchhiking is very common and culturally accepted',
      'Ukrainians are extremely welcoming and generous',
      'Russian and Ukrainian are spoken. Limited English outside Kyiv',
      'WOG and OKKO gas stations are widespread',
      'Roads in variable condition, some damaged by war',
    ],
    phrases: [
      { local: 'Pryvit', meaning: 'Salut (ukrainien)', meaningEn: 'Hi (Ukrainian)' },
      { local: 'Dyakuyu', meaning: 'Merci (ukrainien)', meaningEn: 'Thank you (Ukrainian)' },
      { local: 'Vy yidete do...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'L\'Ukraine a une forte tradition d\'autostop et les Ukrainiens sont tres accueillants. ATTENTION : guerre en cours, zones de conflit a eviter absolument.',
    culturalNotesEn: 'Ukraine has a strong hitchhiking tradition and Ukrainians are very welcoming. WARNING: ongoing war, conflict zones to absolutely avoid.',
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '112' },
  },
  {
    code: 'XK',
    name: 'Kosovo',
    nameEn: 'Kosovo',
    nameEs: 'Kosovo',
    nameDe: 'Kosovo',
    flag: '🇽🇰',
    legality: 'legal',
    legalityText: 'Légal et facile. Petit territoire avec population très accueillante',
    legalityTextEn: 'Legal and easy. Small territory with very welcoming population',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Tres petit territoire, distances courtes',
      'Les Kosovars sont extremement accueillants envers les etrangers',
      'Les stations-service Petrol sont les plus repandues',
      'Beaucoup de diaspora rentre en ete : excellent trafic',
      'L\'albanais est la langue principale, beaucoup parlent allemand ou anglais',
      'Routes en amelioration, autoroutes modernes vers l\'Albanie et Macedoine',
    ],
    tipsEn: [
      'Very small territory, short distances',
      'Kosovars are extremely welcoming to foreigners',
      'Petrol gas stations are most widespread',
      'Lots of diaspora returning in summer: excellent traffic',
      'Albanian is the main language, many speak German or English',
      'Roads improving, modern highways to Albania and Macedonia',
    ],
    phrases: [
      { local: 'Tungjatjeta', meaning: 'Bonjour (albanais)', meaningEn: 'Hello (Albanian)' },
      { local: 'Faleminderit', meaning: 'Merci (albanais)', meaningEn: 'Thank you (Albanian)' },
      { local: 'A shkoni drejt...?', meaning: 'Allez-vous vers...?', meaningEn: 'Are you going toward...?' },
    ],
    culturalNotes: 'Le Kosovo est tres facile pour l\'autostop. Population extremement accueillante. Le trafic de diaspora en ete est un grand avantage.',
    culturalNotesEn: 'Kosovo is very easy for hitchhiking. Extremely welcoming population. Diaspora traffic in summer is a big advantage.',
    emergencyNumbers: { police: '192', ambulance: '194', fire: '193', universal: '112' },
  },
  // ==================== WORLD (non-European) ====================
  {
    code: 'MA',
    name: 'Maroc',
    nameEn: 'Morocco',
    nameEs: 'Marruecos',
    nameDe: 'Marokko',
    flag: '🇲🇦',
    legality: 'legal',
    legalityText: 'Légal et très répandu. Le Maroc est l\'un des meilleurs pays au monde pour l\'autostop.',
    legalityTextEn: 'Legal and very common. Morocco is one of the best countries in the world for hitchhiking.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 10,
    bestMonths: [3, 4, 5, 9, 10, 11],
    tips: [
      'Le pouce leve fonctionne partout, les conducteurs s\'arretent tres facilement',
      'Les camions prennent souvent des autostoppeurs sur les longues distances',
      'On vous offrira souvent le the a la menthe, accepter est un signe de respect',
      'Negociez le prix avant de monter dans les grands taxis (transport collectif)',
      'Les stations-service sont d\'excellents spots, surtout aux sorties de villes',
      'Apprenez quelques mots d\'arabe ou de darija : "choukran" (merci), "bslama" (au revoir)',
    ],
    laws: [
      'Aucune restriction legale, l\'autostop est une pratique courante et acceptee',
    ],
    emergencyNumbers: { police: '19', ambulance: '15', fire: '15', universal: '112' },
  },
  {
    code: 'TR',
    name: 'Turquie',
    nameEn: 'Turkey',
    nameEs: 'Turquía',
    nameDe: 'Türkei',
    flag: '🇹🇷',
    legality: 'legal',
    legalityText: 'Légal et culturellement accepté. L\'hospitalite turque rend l\'autostop tres agreable.',
    legalityTextEn: 'Legal and culturally accepted. Turkish hospitality makes hitchhiking very pleasant.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 15,
    bestMonths: [4, 5, 6, 9, 10],
    tips: [
      'L\'hospitalite turque est legendaire : on vous invitera souvent a manger ou dormir',
      'Les stations de peage (otoyol) sont les meilleurs spots pour les longues distances',
      'Les camionneurs (TIR) sont tres accueillants et parcourent de longues distances',
      'Apprenez "otoban" (autoroute) et "nereye?" (ou allez-vous?)',
      'Le the (cay) offert est un rituel, ne refusez jamais',
      'Les stations-service Total et BP sont ideales pour aborder les conducteurs',
    ],
    laws: [
      'Pas de restriction. Les policiers aident parfois les autostoppeurs.',
    ],
    emergencyNumbers: { police: '155', ambulance: '112', fire: '110', universal: '112' },
  },
  {
    code: 'US',
    name: 'États-Unis',
    nameEn: 'United States',
    nameEs: 'Estados Unidos',
    nameDe: 'Vereinigte Staaten',
    flag: '🇺🇸',
    legality: 'varies',
    legalityText: 'Legalite variable selon les États. Interdit sur les Interstate highways, tolere sur les bretelles.',
    legalityTextEn: 'Legality varies by state. Prohibited on Interstate highways, tolerated on on-ramps.',
    difficulty: 3,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Difficult',
    avgWaitTime: 90,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Chaque Etat a ses propres lois : verifiez avant de partir',
      'Les truck stops (TA, Pilot, Love\'s) sont les meilleurs endroits pour demander',
      'Un panneau avec votre destination est quasi obligatoire',
      'Les grandes distances entre villes rendent le trajet long',
      'La cote Ouest (Pacific Coast Highway) est plus facile que le Midwest',
      'Les festivals et evenements attirent des conducteurs bienveillants',
    ],
    laws: [
      'Interdit sur les Interstate highways dans la plupart des Etats',
      'Legal sur les bretelles d\'acces dans de nombreux Etats',
      'Verifiez les lois locales : certains Etats l\'interdisent completement',
    ],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  {
    code: 'CA',
    name: 'Canada',
    nameEn: 'Canada',
    nameEs: 'Canadá',
    nameDe: 'Kanada',
    flag: '🇨🇦',
    legality: 'legal',
    legalityText: 'Légal dans la plupart des provinces sauf sur les autoroutes a acces limite.',
    legalityTextEn: 'Legal in most provinces except on limited-access highways.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 45,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'La Trans-Canada Highway est la route classique pour traverser le pays',
      'Les Canadiens sont tres accueillants, surtout dans les Maritimes et en BC',
      'Les distances sont enormes : preparez-vous a de longues attentes dans les Prairies',
      'Les stations-service Tim Hortons sont de bons points de rencontre',
      'L\'Alberta est plus difficile, le Quebec francophone est tres accueillant',
    ],
    laws: [
      'Legal dans la plupart des provinces',
      'Interdit sur les autoroutes a acces limite en Ontario et Colombie-Britannique',
    ],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  {
    code: 'NZ',
    name: 'Nouvelle-Zélande',
    nameEn: 'New Zealand',
    nameEs: 'Nueva Zelanda',
    nameDe: 'Neuseeland',
    flag: '🇳🇿',
    legality: 'legal',
    legalityText: 'Completement legal et très courant. L\'un des meilleurs pays au monde pour l\'autostop.',
    legalityTextEn: 'Completely legal and very common. One of the best countries in the world for hitchhiking.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 15,
    bestMonths: [11, 12, 1, 2, 3],
    tips: [
      'Probablement le meilleur pays au monde pour l\'autostop',
      'Les kiwis s\'arretent tres facilement, surtout en zone rurale',
      'L\'ile du Sud est plus facile que l\'ile du Nord',
      'Les backpackers hitchhikent enormement, c\'est dans la culture',
      'Emportez un panneau, ca accelere beaucoup les choses',
      'Les distances sont courtes, vous pouvez traverser une ile en quelques jours',
    ],
    laws: [
      'Aucune restriction. L\'autostop est une tradition nationale.',
    ],
    emergencyNumbers: { police: '111', ambulance: '111', fire: '111', universal: '111' },
  },
  {
    code: 'AU',
    name: 'Australie',
    nameEn: 'Australia',
    nameEs: 'Australia',
    nameDe: 'Australien',
    flag: '🇦🇺',
    legality: 'varies',
    legalityText: 'Légal dans la plupart des États sauf Queensland et Victoria. Distances enormes.',
    legalityTextEn: 'Legal in most states except Queensland and Victoria. Enormous distances.',
    difficulty: 3,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Difficult',
    avgWaitTime: 60,
    bestMonths: [3, 4, 5, 9, 10, 11],
    tips: [
      'Les distances sont enormes : preparez eau, nourriture et protection solaire',
      'Les road trains (camions geants) ne s\'arretent pas facilement',
      'La cote Est (Sydney-Brisbane-Cairns) est la plus facile',
      'Les stations-service et truck stops sont vos meilleurs amis',
      'Le Northern Territory et le WA sont tres isoles, soyez prudent',
      'Les Australiens sont amicaux mais l\'autostop a decline depuis les annees 2000',
    ],
    laws: [
      'Restreint dans le Queensland (interdit sur certaines routes) et Victoria (deconseille)',
      'Legal dans les autres Etats mais evitez les autoroutes a peage',
      'Ayez toujours de l\'eau et de la protection solaire',
    ],
    emergencyNumbers: { police: '000', ambulance: '000', fire: '000', universal: '000' },
  },
  {
    code: 'GE',
    name: 'Géorgie',
    nameEn: 'Georgia',
    nameEs: 'Georgia',
    nameDe: 'Georgien',
    flag: '🇬🇪',
    legality: 'legal',
    legalityText: 'Légal et extremement facile. L\'hospitalite georgienne est legendaire.',
    legalityTextEn: 'Legal and extremely easy. Georgian hospitality is legendary.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 5,
    bestMonths: [4, 5, 6, 7, 8, 9, 10],
    tips: [
      'L\'un des pays les plus accueillants au monde pour l\'autostop',
      'On vous invitera tres souvent a manger ou dormir chez l\'habitant',
      'La route militaire de Georgie (Tbilissi-Kazbegi) est mythique',
      'Les marshrutkas (minibus) sont une alternative pas chere',
      'Apprenez "gamarjoba" (bonjour) et "madloba" (merci)',
    ],
    laws: [
      'Aucune restriction. L\'autostop est naturel et tres pratique.',
    ],
    emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'IL',
    name: 'Israel',
    nameEn: 'Israel',
    nameEs: 'Israel',
    nameDe: 'Israel',
    flag: '🇮🇱',
    legality: 'legal',
    legalityText: 'Légal et courant, surtout chez les jeunes et les soldats. Le trempistim (autostop) est une tradition.',
    legalityTextEn: 'Legal and common, especially among youth and soldiers. Tremping is a tradition.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 10,
    bestMonths: [3, 4, 5, 10, 11],
    tips: [
      'Le "tremping" est une tradition nationale, surtout dans le sud',
      'Les trempiadot (arrets d\'autostop officiels) sont marques',
      'Les soldats hitchhikent enormement, c\'est tres normal',
      'Les distances sont courtes, tout le pays se traverse en quelques heures',
      'Evitez de faire de l\'autostop le vendredi soir et samedi (Shabbat)',
    ],
    tipsEn: [
      'Tremping is a national tradition, especially in the south',
      'Trempiadot (official hitchhiking stops) are marked',
      'Soldiers hitch very frequently, it\'s very normal',
      'Distances are short, entire country can be crossed in hours',
      'Avoid hitchhiking Friday evening and Saturday (Shabbat)',
    ],
    laws: [
      'Legal. Des arrets officiels (trempiadot) existent sur de nombreuses routes.',
    ],
    lawsEn: [
      'Legal. Official stops (trempiadot) exist on many roads.',
    ],
    phrases: [
      { local: 'Shalom', meaning: 'Bonjour/Salut', meaningEn: 'Hello/Hi' },
      { local: 'Toda', meaning: 'Merci', meaningEn: 'Thank you' },
    ],
    culturalNotes: 'Israel est excellent pour l\'autostop. Le "tremping" est une tradition nationale avec des arrets officiels. Les soldats hitchhikent tres frequemment.',
    culturalNotesEn: 'Israel is excellent for hitchhiking. "Tremping" is a national tradition with official stops. Soldiers hitch very frequently.',
    emergencyNumbers: { police: '100', ambulance: '101', fire: '102', universal: '112' },
  },
  {
    code: 'AR',
    name: 'Argentine',
    nameEn: 'Argentina',
    nameEs: 'Argentina',
    nameDe: 'Argentinien',
    flag: '🇦🇷',
    legality: 'legal',
    legalityText: 'Légal et très répandu. L\'un des meilleurs pays d\'Amerique du Sud pour l\'autostop.',
    legalityTextEn: 'Legal and very common. One of the best countries in South America for hitchhiking.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 30,
    bestMonths: [10, 11, 12, 1, 2, 3],
    tips: [
      'L\'autostop ("hacer dedo") est culturellement accepte et commun',
      'La Patagonie est particulierement facile grace aux touristes',
      'Les stations-service YPF sont d\'excellents spots',
      'Les Argentins sont tres accueillants et curieux',
      'Distances enormes : preparez-vous a de longs trajets',
      'Le mate (the traditionnel) vous sera souvent offert',
    ],
    tipsEn: [
      'Hitchhiking ("hacer dedo") is culturally accepted and common',
      'Patagonia is particularly easy thanks to tourists',
      'YPF gas stations are excellent spots',
      'Argentines are very welcoming and curious',
      'Enormous distances: prepare for long journeys',
      'Mate (traditional tea) will often be offered',
    ],
    phrases: [
      { local: 'Hola! Vas hacia...?', meaning: 'Salut! Tu vas vers...?', meaningEn: 'Hi! Are you going toward...?' },
      { local: 'Gracias!', meaning: 'Merci!', meaningEn: 'Thank you!' },
      { local: 'Estoy haciendo dedo', meaning: 'Je fais de l\'autostop', meaningEn: 'I\'m hitchhiking' },
    ],
    culturalNotes: 'L\'Argentine est excellente pour l\'autostop. Les Argentins sont accueillants et le mate partage cree des liens. La Patagonie est legendaire.',
    culturalNotesEn: 'Argentina is excellent for hitchhiking. Argentines are welcoming and sharing mate creates bonds. Patagonia is legendary.',
    emergencyNumbers: { police: '911', ambulance: '107', fire: '100', universal: '911' },
  },
  {
    code: 'CL',
    name: 'Chili',
    nameEn: 'Chile',
    nameEs: 'Chile',
    nameDe: 'Chile',
    flag: '🇨🇱',
    legality: 'legal',
    legalityText: 'Légal et assez facile. Tres bon dans le sud (Patagonie), plus difficile au centre',
    legalityTextEn: 'Legal and fairly easy. Very good in the south (Patagonia), harder in center',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 35,
    bestMonths: [10, 11, 12, 1, 2, 3],
    tips: [
      'Le sud (Patagonie chilienne) est excellent, beaucoup de touristes',
      'Le centre (Santiago-Valparaiso) est plus difficile',
      'Les stations-service Copec et Shell sont de bons spots',
      'La Carretera Austral est mythique mais trafic tres faible',
      'Les Chiliens sont polis mais plus reserves que les Argentins',
      'Distances enormes du nord au sud (4300 km)',
    ],
    tipsEn: [
      'The south (Chilean Patagonia) is excellent, lots of tourists',
      'The center (Santiago-Valparaiso) is more difficult',
      'Copec and Shell gas stations are good spots',
      'Carretera Austral is legendary but very low traffic',
      'Chileans are polite but more reserved than Argentines',
      'Enormous distances north to south (4300km)',
    ],
    phrases: [
      { local: 'Hola! Va hacia...?', meaning: 'Salut! Vous allez vers...?', meaningEn: 'Hi! Are you going toward...?' },
      { local: 'Gracias!', meaning: 'Merci!', meaningEn: 'Thank you!' },
    ],
    culturalNotes: 'Le Chili est bon pour l\'autostop surtout dans le sud. Les Chiliens sont polis. La Patagonie et la Carretera Austral sont mythiques.',
    culturalNotesEn: 'Chile is good for hitchhiking especially in the south. Chileans are polite. Patagonia and Carretera Austral are legendary.',
    emergencyNumbers: { police: '133', ambulance: '131', fire: '132', universal: '911' },
  },
  {
    code: 'CO',
    name: 'Colombie',
    nameEn: 'Colombia',
    nameEs: 'Colombia',
    nameDe: 'Kolumbien',
    flag: '🇨🇴',
    legality: 'legal',
    legalityText: 'Légal. Possible mais necessite prudence. Beaucoup mieux qu\'auparavant.',
    legalityTextEn: 'Legal. Possible but requires caution. Much better than before.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 40,
    bestMonths: [12, 1, 2, 6, 7, 8],
    tips: [
      'La securite s\'est enormement amelioree depuis les annees 2000',
      'Les Colombiens sont extremement accueillants ("el mas chevere")',
      'Evitez l\'autostop de nuit et dans certaines zones rurales',
      'Les camionneurs sont souvent de bons conducteurs',
      'Les zones touristiques (eje cafetero, cote) sont plus faciles',
      'Beaucoup de barrages militaires : ayez toujours vos papiers',
    ],
    tipsEn: [
      'Security has greatly improved since the 2000s',
      'Colombians are extremely welcoming ("el mas chevere")',
      'Avoid hitchhiking at night and in some rural areas',
      'Truck drivers are often good drivers',
      'Tourist areas (coffee region, coast) are easier',
      'Many military checkpoints: always have your documents',
    ],
    phrases: [
      { local: 'Hola! Que mas?', meaning: 'Salut! Quoi de neuf?', meaningEn: 'Hi! What\'s up?' },
      { local: 'Muchas gracias parce!', meaning: 'Merci beaucoup mec!', meaningEn: 'Thank you very much dude!' },
    ],
    culturalNotes: 'La Colombie est possible avec prudence. Les Colombiens sont tres accueillants. La situation securitaire s\'est beaucoup amelioree.',
    culturalNotesEn: 'Colombia is possible with caution. Colombians are very welcoming. Security situation has greatly improved.',
    emergencyNumbers: { police: '123', ambulance: '125', fire: '119', universal: '123' },
  },
  {
    code: 'TH',
    name: 'Thaïlande',
    nameEn: 'Thailand',
    nameEs: 'Tailandia',
    nameDe: 'Thailand',
    flag: '🇹🇭',
    legality: 'legal',
    legalityText: 'Légal mais peu commun. Possible dans les zones touristiques et le nord',
    legalityTextEn: 'Legal but uncommon. Possible in tourist areas and the north',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 45,
    bestMonths: [11, 12, 1, 2],
    tips: [
      'L\'autostop n\'est pas courant mais les Thais sont serviables',
      'Le nord (Chiang Mai, Chiang Rai) est plus facile',
      'Les stations-service PTT sont de bons spots',
      'Le pouce leve n\'est pas compris : tendez la main paume vers le bas',
      'Peu d\'anglais hors zones touristiques, apprenez quelques mots de thai',
      'Les bus sont tres bon marche, parfois plus pratiques que l\'autostop',
    ],
    tipsEn: [
      'Hitchhiking is uncommon but Thais are helpful',
      'The north (Chiang Mai, Chiang Rai) is easier',
      'PTT gas stations are good spots',
      'Thumb up not understood: extend hand palm down',
      'Little English outside tourist areas, learn some Thai words',
      'Buses are very cheap, sometimes more practical than hitchhiking',
    ],
    phrases: [
      { local: 'Sawasdee krap/ka', meaning: 'Bonjour (homme/femme)', meaningEn: 'Hello (male/female)' },
      { local: 'Khop khun krap/ka', meaning: 'Merci (homme/femme)', meaningEn: 'Thank you (male/female)' },
      { local: 'Pai...dai mai?', meaning: 'Puis-je aller a...?', meaningEn: 'Can I go to...?' },
    ],
    culturalNotes: 'La Thailande est moyennement facile pour l\'autostop. Les Thais sont serviables mais l\'autostop n\'est pas dans la culture. Le nord est plus facile.',
    culturalNotesEn: 'Thailand is moderately easy for hitchhiking. Thais are helpful but hitchhiking is not in the culture. The north is easier.',
    emergencyNumbers: { police: '191', ambulance: '1669', fire: '199', universal: '191' },
  },
  {
    code: 'IN',
    name: 'Inde',
    nameEn: 'India',
    nameEs: 'India',
    nameDe: 'Indien',
    flag: '🇮🇳',
    legality: 'varies',
    legalityText: 'Légal dans la plupart des États. Difficile mais possible, enormes differences regionales',
    legalityTextEn: 'Legal in most states. Difficult but possible, huge regional differences',
    difficulty: 4,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Difficult',
    avgWaitTime: 60,
    bestMonths: [10, 11, 12, 1, 2, 3],
    tips: [
      'Enormes differences regionales : Himachal/Ladakh faciles, grandes villes difficiles',
      'L\'autostop n\'est pas commun sauf dans certaines regions montagneuses',
      'Les routiers sikhs sont connus pour leur aide aux voyageurs',
      'Attention aux differences culturelles, surtout pour les femmes',
      'Les stations-service Bharat Petroleum et Indian Oil sont de bons spots',
      'Distances enormes, trafic dense et chaotique',
    ],
    tipsEn: [
      'Huge regional differences: Himachal/Ladakh easy, big cities difficult',
      'Hitchhiking is uncommon except in some mountain regions',
      'Sikh truckers are known for helping travelers',
      'Watch cultural differences, especially for women',
      'Bharat Petroleum and Indian Oil stations are good spots',
      'Enormous distances, dense and chaotic traffic',
    ],
    phrases: [
      { local: 'Namaste', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Dhanyavaad', meaning: 'Merci (hindi)', meaningEn: 'Thank you (Hindi)' },
      { local: 'Aap...ja rahe hain?', meaning: 'Allez-vous a...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'L\'Inde est difficile pour l\'autostop mais possible. Enormes differences regionales. L\'Himachal Pradesh et le Ladakh sont legendaires. Respect culturel essentiel.',
    culturalNotesEn: 'India is difficult for hitchhiking but possible. Huge regional differences. Himachal Pradesh and Ladakh are legendary. Cultural respect essential.',
    emergencyNumbers: { police: '100', ambulance: '102', fire: '101', universal: '112' },
  },
  {
    code: 'JP',
    name: 'Japon',
    nameEn: 'Japan',
    nameEs: 'Japón',
    nameDe: 'Japan',
    flag: '🇯🇵',
    legality: 'legal',
    legalityText: 'Légal mais très peu commun. Possible avec patience et panneaux en japonais',
    legalityTextEn: 'Legal but very uncommon. Possible with patience and signs in Japanese',
    difficulty: 4,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Difficult',
    avgWaitTime: 90,
    bestMonths: [4, 5, 9, 10],
    tips: [
      'L\'autostop est tres rare au Japon, beaucoup de Japonais sont surpris',
      'Un panneau en japonais est quasi obligatoire',
      'Les aires d\'autoroute (SA/PA) sont les meilleurs spots',
      'Hokkaido est la region la plus facile',
      'Les Japonais sont polis mais peuvent hesiter a prendre des etrangers',
      'Les transports publics sont excellents et ponctuels',
    ],
    tipsEn: [
      'Hitchhiking is very rare in Japan, many Japanese are surprised',
      'A sign in Japanese is almost mandatory',
      'Highway rest areas (SA/PA) are the best spots',
      'Hokkaido is the easiest region',
      'Japanese are polite but may hesitate to pick up foreigners',
      'Public transport is excellent and punctual',
    ],
    phrases: [
      { local: 'Konnichiwa', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Arigato gozaimasu', meaning: 'Merci beaucoup', meaningEn: 'Thank you very much' },
      { local: '...made ikimasuka?', meaning: 'Allez-vous jusqu\'a...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'Le Japon est difficile pour l\'autostop car peu pratique. Un panneau en japonais et beaucoup de patience sont necessaires. Hokkaido est plus facile.',
    culturalNotesEn: 'Japan is difficult for hitchhiking as it\'s uncommon. A sign in Japanese and lots of patience are necessary. Hokkaido is easier.',
    emergencyNumbers: { police: '110', ambulance: '119', fire: '119', universal: '110' },
  },
  {
    code: 'ZA',
    name: 'Afrique du Sud',
    nameEn: 'South Africa',
    nameEs: 'Sudáfrica',
    nameDe: 'Südafrika',
    flag: '🇿🇦',
    legality: 'legal',
    legalityText: 'Légal mais necessite prudence. Pratique courante dans les townships, moins pour touristes',
    legalityTextEn: 'Legal but requires caution. Common practice in townships, less for tourists',
    difficulty: 4,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Difficult',
    avgWaitTime: 60,
    bestMonths: [10, 11, 12, 1, 2, 3],
    tips: [
      'SECURITE : evitez l\'autostop de nuit et dans certaines zones',
      'L\'autostop est courant dans les townships mais differente culture',
      'Les routes touristiques (Garden Route) sont plus sures',
      'Les stations-service Engen et Shell sont de bons spots de jour',
      'Les Sud-Africains sont accueillants mais prudents',
      'Beaucoup parlent anglais (et afrikaans, zoulou, xhosa...)',
    ],
    tipsEn: [
      'SECURITY: avoid hitchhiking at night and in certain areas',
      'Hitchhiking is common in townships but different culture',
      'Tourist routes (Garden Route) are safer',
      'Engen and Shell gas stations are good daytime spots',
      'South Africans are welcoming but cautious',
      'Many speak English (and Afrikaans, Zulu, Xhosa...)',
    ],
    phrases: [
      { local: 'Howzit?', meaning: 'Salut? (informel)', meaningEn: 'Hi? (informal)' },
      { local: 'Thanks!', meaning: 'Merci!', meaningEn: 'Thank you!' },
    ],
    culturalNotes: 'L\'Afrique du Sud necessite prudence. L\'autostop existe mais questions de securite importantes. Routes touristiques plus sures. Culture riche et diverse.',
    culturalNotesEn: 'South Africa requires caution. Hitchhiking exists but important security concerns. Tourist routes safer. Rich and diverse culture.',
    emergencyNumbers: { police: '10111', ambulance: '10177', fire: '10111', universal: '112' },
  },
  {
    code: 'IR',
    name: 'Iran',
    nameEn: 'Iran',
    nameEs: 'Irán',
    nameDe: 'Iran',
    flag: '🇮🇷',
    legality: 'legal',
    legalityText: 'Légal et facile. L\'hospitalite iranienne est legendaire.',
    legalityTextEn: 'Legal and easy. Iranian hospitality is legendary.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [3, 4, 5, 9, 10, 11],
    tips: [
      'L\'hospitalite iranienne est legendaire, on vous invitera souvent a manger/dormir',
      'Les Iraniens sont extremement curieux et accueillants envers les etrangers',
      'Les stations-service sont d\'excellents spots',
      'Apprenez quelques mots de persan : "mamnoon" (merci), "salam" (bonjour)',
      'Les jeunes parlent souvent anglais',
      'Visa requis, verifiez restrictions selon votre nationalite',
    ],
    tipsEn: [
      'Iranian hospitality is legendary, you\'ll often be invited to eat/sleep',
      'Iranians are extremely curious and welcoming to foreigners',
      'Gas stations are excellent spots',
      'Learn some Persian words: "mamnoon" (thank you), "salam" (hello)',
      'Young people often speak English',
      'Visa required, check restrictions for your nationality',
    ],
    phrases: [
      { local: 'Salam', meaning: 'Bonjour', meaningEn: 'Hello' },
      { local: 'Mamnoon', meaning: 'Merci', meaningEn: 'Thank you' },
      { local: 'Shoma be...mirid?', meaning: 'Allez-vous a...?', meaningEn: 'Are you going to...?' },
    ],
    culturalNotes: 'L\'Iran est excellent pour l\'autostop. L\'hospitalite iranienne est legendaire. Les Iraniens sont extremement accueillants. Visa requis.',
    culturalNotesEn: 'Iran is excellent for hitchhiking. Iranian hospitality is legendary. Iranians are extremely welcoming. Visa required.',
    emergencyNumbers: { police: '110', ambulance: '115', fire: '125', universal: '112' },
  },
  // ==================== NORTH AFRICA ====================
  {
    code: 'TN',
    name: 'Tunisie',
    nameEn: 'Tunisia',
    nameEs: 'Túnez',
    nameDe: 'Tunesien',
    flag: '🇹🇳',
    legality: 'legal',
    legalityText: 'Légal et courant. L\'hospitalite tunisienne rend l\'autostop facile.',
    legalityTextEn: 'Legal and common. Tunisian hospitality makes hitchhiking easy.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [3, 4, 5, 9, 10, 11],
    tips: [
      'Le francais est tres repandu, pas de barriere linguistique pour les francophones',
      'Les louages (taxis collectifs) sont le transport principal entre villes',
      'Les stations-service aux sorties de villes sont les meilleurs spots',
      'Le the a la menthe offert est un signe d\'hospitalite, ne refuse pas',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '197', ambulance: '190', fire: '198', universal: '112' },
  },
  // ==================== LATIN AMERICA ====================
  {
    code: 'MX',
    name: 'Mexique',
    nameEn: 'Mexico',
    nameEs: 'México',
    nameDe: 'Mexiko',
    flag: '🇲🇽',
    legality: 'legal',
    legalityText: 'Légal. Pratique courante en zone rurale. Evite le nord du pays.',
    legalityTextEn: 'Legal. Common practice in rural areas. Avoid northern states.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [11, 12, 1, 2, 3, 4],
    tips: [
      'Les casetas (peages) et les stations Pemex sont les meilleurs spots',
      'Les "trailers" (camions) font de longues distances',
      'Evite absolument le nord (Sinaloa, Tamaulipas)',
      'Le sud (Oaxaca, Chiapas, Yucatan) est le plus facile et le plus sur',
      'Ne voyage JAMAIS de nuit sur les routes du nord',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  {
    code: 'BR',
    name: 'Brésil',
    nameEn: 'Brazil',
    nameEs: 'Brasil',
    nameDe: 'Brasilien',
    flag: '🇧🇷',
    legality: 'legal',
    legalityText: 'Légal. Possible mais les distances sont immenses. Le sud est le plus facile.',
    legalityTextEn: 'Legal. Possible but distances are huge. The south is easiest.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 45,
    bestMonths: [3, 4, 5, 9, 10, 11],
    tips: [
      'Le portugais est indispensable, ne parle PAS espagnol',
      'Les stations-service sont les meilleurs spots',
      'Les camionneurs sont les plus fiables pour les longues distances',
      'Le sud (Rio Grande do Sul, Santa Catarina) est le plus facile',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '190', ambulance: '192', fire: '193', universal: '190' },
  },
  {
    code: 'PE',
    name: 'Pérou',
    nameEn: 'Peru',
    nameEs: 'Perú',
    nameDe: 'Peru',
    flag: '🇵🇪',
    legality: 'legal',
    legalityText: 'Légal et courant. Les camions dans les Andes prennent des passagers regulierement.',
    legalityTextEn: 'Legal and common. Trucks in the Andes regularly take passengers.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 30,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Les garitas (controles routiers) et les grifos (stations) sont les meilleurs spots',
      'Les camions dans les Andes s\'arretent facilement',
      'Clarifie "gratis" avant de monter (transport informel payant courant)',
      'Le mal d\'altitude est reel a Cusco (3 400m) et au-dessus',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '105', ambulance: '116', fire: '116', universal: '105' },
  },
  {
    code: 'BO',
    name: 'Bolivie',
    nameEn: 'Bolivia',
    nameEs: 'Bolivia',
    nameDe: 'Bolivien',
    flag: '🇧🇴',
    legality: 'legal',
    legalityText: 'Légal et courant. Les camions sont un mode de transport normal dans les Andes.',
    legalityTextEn: 'Legal and common. Trucks are a normal mode of transport in the Andes.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 30,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Le pays le moins cher d\'Amerique du Sud',
      'Les camions prennent des passagers (parfois contre un petit paiement)',
      'Le mal d\'altitude est serieux (La Paz est a 3 640m)',
      'L\'Altiplano a peu de trafic mais tout le monde s\'arrete',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '110', ambulance: '118', fire: '119', universal: '110' },
  },
  {
    code: 'EC',
    name: 'Équateur',
    nameEn: 'Ecuador',
    nameEs: 'Ecuador',
    nameDe: 'Ecuador',
    flag: '🇪🇨',
    legality: 'legal',
    legalityText: 'Légal et facile. Petit pays, distances courtes, gens accueillants.',
    legalityTextEn: 'Legal and easy. Small country, short distances, welcoming people.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [6, 7, 8, 9],
    tips: [
      'Monte dans les bennes des pickups (camionetas), c\'est normal et courant',
      'Le dollar americain est la monnaie officielle',
      'Les peages et stations-service sont les meilleurs spots',
      'Le pays est petit et se traverse en quelques heures',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  {
    code: 'UY',
    name: 'Uruguay',
    nameEn: 'Uruguay',
    nameEs: 'Uruguay',
    nameDe: 'Uruguay',
    flag: '🇺🇾',
    legality: 'legal',
    legalityText: 'Légal et facile. Le pays le plus sur d\'Amerique du Sud.',
    legalityTextEn: 'Legal and easy. The safest country in South America.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [11, 12, 1, 2, 3],
    tips: [
      'Le pays le plus sur d\'Amerique du Sud',
      'Les stations Ancap et les peages sont les meilleurs spots',
      'Le mate est la religion nationale, accepte si on t\'en propose',
      'En ete la cote est tres frequentee par les Argentins',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  // ==================== SOUTHEAST ASIA ====================
  {
    code: 'VN',
    name: 'Vietnam',
    nameEn: 'Vietnam',
    nameEs: 'Vietnam',
    nameDe: 'Vietnam',
    flag: '🇻🇳',
    legality: 'legal',
    legalityText: 'Pas de concept formel d\'autostop mais les Vietnamiens sont naturellement serviables.',
    legalityTextEn: 'No formal hitchhiking concept but Vietnamese are naturally helpful.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [2, 3, 4, 10, 11],
    tips: [
      'Le pouce leve ne fonctionne pas. Tends la main paume vers le bas.',
      'Beaucoup de backpackers font le Vietnam en moto achetee sur place',
      'Le nord montagneux (Ha Giang, Sapa) est plus facile pour le stop',
      'La nourriture de rue est parmi les meilleures au monde et tres bon marche',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '113', ambulance: '115', fire: '114', universal: '113' },
  },
  {
    code: 'LA',
    name: 'Laos',
    nameEn: 'Laos',
    nameEs: 'Laos',
    nameDe: 'Laos',
    flag: '🇱🇦',
    legality: 'legal',
    legalityText: 'Pas de réglementation. Le transport informel est courant en zone rurale.',
    legalityTextEn: 'No regulation. Informal transport is common in rural areas.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [11, 12, 1, 2],
    tips: [
      'Le pays le plus detendu d\'Asie du Sud-Est',
      'Les sawngthaws (camions-bus) s\'arretent partout et coutent presque rien',
      'JAMAIS quitter les sentiers en zone rurale (bombes non explosees)',
      'Le slow boat sur le Mekong est une experience inoubliable',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '1191', ambulance: '1195', fire: '1190', universal: '1191' },
  },
  {
    code: 'KH',
    name: 'Cambodge',
    nameEn: 'Cambodia',
    nameEs: 'Camboya',
    nameDe: 'Kambodscha',
    flag: '🇰🇭',
    legality: 'legal',
    legalityText: 'Pas de réglementation. Le transport informel est courant.',
    legalityTextEn: 'No regulation. Informal transport is common.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 25,
    bestMonths: [11, 12, 1, 2, 3],
    tips: [
      'Le dollar americain est la monnaie de facto',
      'Les guesthouses sont si bon marche (3-5 $) que le camping n\'est pas necessaire',
      'Angkor Wat est une merveille du monde, ne le manque pas',
      'Ne parle jamais des Khmers rouges de facon legere',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '117', ambulance: '119', fire: '118', universal: '117' },
  },
  // ==================== SOUTH ASIA ====================
  {
    code: 'NP',
    name: 'Népal',
    nameEn: 'Nepal',
    nameEs: 'Nepal',
    nameDe: 'Nepal',
    flag: '🇳🇵',
    legality: 'legal',
    legalityText: 'Pas de réglementation. Le transport informel (camions, pickups) est un mode de vie.',
    legalityTextEn: 'No regulation. Informal transport (trucks, pickups) is a way of life.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [10, 11, 3, 4],
    tips: [
      'Le dhal bhat (riz et lentilles) est a volonte dans beaucoup de restaurants',
      'Les camions Tata sur les routes de montagne prennent des passagers',
      'Le Namaste avec les mains jointes est la salutation universelle',
      'Octobre-novembre est la meilleure periode pour les vues sur l\'Himalaya',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '102' },
  },
  // ==================== CENTRAL ASIA ====================
  {
    code: 'KZ',
    name: 'Kazakhstan',
    nameEn: 'Kazakhstan',
    nameEs: 'Kazajistán',
    nameDe: 'Kasachstan',
    flag: '🇰🇿',
    legality: 'legal',
    legalityText: 'Légal. Le transport informel est courant dans ce pays immense.',
    legalityTextEn: 'Legal. Informal transport is common in this huge country.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [5, 6, 9],
    tips: [
      'Le russe est indispensable, l\'anglais est tres rare',
      'Les distances sont immenses (9eme plus grand pays au monde)',
      'Les camions sont les meilleurs pour les longues distances',
      'Le camping sauvage est facile dans les steppes infinies',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '112' },
  },
  {
    code: 'KG',
    name: 'Kirghizistan',
    nameEn: 'Kyrgyzstan',
    nameEs: 'Kirguistán',
    nameDe: 'Kirgisistan',
    flag: '🇰🇬',
    legality: 'legal',
    legalityText: 'Légal et facile. Le transport informel est un mode de vie en montagne.',
    legalityTextEn: 'Legal and easy. Informal transport is a way of life in the mountains.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [6, 7, 8, 9],
    tips: [
      'Le lac Issyk-Kul est magnifique en ete',
      'Les yourtes des bergers accueillent souvent les voyageurs',
      'Le koumiss (lait de jument fermente) est la boisson traditionnelle',
      'Le CBT (Community Based Tourism) organise des sejours en yourte',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '112' },
  },
  {
    code: 'UZ',
    name: 'Ouzbékistan',
    nameEn: 'Uzbekistan',
    nameEs: 'Uzbekistán',
    nameDe: 'Usbekistan',
    flag: '🇺🇿',
    legality: 'legal',
    legalityText: 'Légal. Le transport semi-informel (voitures privees faisant taxi) est la norme.',
    legalityTextEn: 'Legal. Semi-informal transport (private cars acting as taxis) is the norm.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 15,
    bestMonths: [4, 5, 9, 10],
    tips: [
      'Le coeur de la Route de la Soie (Samarkand, Boukhara, Khiva)',
      'Lever la main au bord de la route arrete des voitures privees qui font taxi',
      'Clarifie "besplatno" ou "bepul" (gratuit) AVANT de monter',
      'Le plov (riz pilaf) est le plat national, servi le jeudi',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '112' },
  },
  // ==================== MIDDLE EAST ====================
  {
    code: 'JO',
    name: 'Jordanie',
    nameEn: 'Jordan',
    nameEs: 'Jordania',
    nameDe: 'Jordanien',
    flag: '🇯🇴',
    legality: 'legal',
    legalityText: 'Légal et facile. L\'hospitalite jordanienne rend l\'autostop tres agreable.',
    legalityTextEn: 'Legal and easy. Jordanian hospitality makes hitchhiking very pleasant.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 10,
    bestMonths: [3, 4, 5, 10, 11],
    tips: [
      'Les Jordaniens s\'arretent tres facilement et sont curieux des etrangers',
      'Beaucoup de conducteurs refusent l\'argent et insistent pour t\'inviter',
      'La King\'s Highway est magnifique pour le stop',
      'Le Wadi Rum est ideal pour le camping sous les etoiles',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  {
    code: 'OM',
    name: 'Oman',
    nameEn: 'Oman',
    nameEs: 'Omán',
    nameDe: 'Oman',
    flag: '🇴🇲',
    legality: 'legal',
    legalityText: 'Légal et très facile. Les Omanais s\'arretent avant meme que tu leves le pouce.',
    legalityTextEn: 'Legal and very easy. Omanis stop before you even raise your thumb.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 10,
    bestMonths: [10, 11, 12, 1, 2, 3],
    tips: [
      'L\'un des pays les plus surs au monde',
      'Les conducteurs font des detours importants pour t\'aider',
      'Le camping sauvage est legal et populaire (wadis, plages, desert)',
      'Le cafe omanais et les dattes sont offerts a chaque rencontre',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '9999', ambulance: '9999', fire: '9999', universal: '9999' },
  },
  // ==================== AFRICA ====================
  {
    code: 'SN',
    name: 'Sénégal',
    nameEn: 'Senegal',
    nameEs: 'Senegal',
    nameDe: 'Senegal',
    flag: '🇸🇳',
    legality: 'legal',
    legalityText: 'Légal. La teranga (hospitalite) fait que les conducteurs s\'arretent naturellement.',
    legalityTextEn: 'Legal. Teranga (hospitality) means drivers stop naturally.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [11, 12, 1, 2, 3, 4],
    tips: [
      'Le francais est parle partout, pas de barriere linguistique',
      'La teranga (hospitalite) est l\'ame du Senegal',
      'Les sept-places et ndiaga ndiaye sont le transport principal',
      'Le thieboudienne (riz au poisson) est le plat national',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '17', ambulance: '15', fire: '18', universal: '112' },
  },
  {
    code: 'NA',
    name: 'Namibie',
    nameEn: 'Namibia',
    nameEs: 'Namibia',
    nameDe: 'Namibia',
    flag: '🇳🇦',
    legality: 'legal',
    legalityText: 'Légal mais les distances sont immenses et le trafic très faible.',
    legalityTextEn: 'Legal but distances are huge and traffic very low.',
    difficulty: 4,
    difficultyText: 'Difficile',
    difficultyTextEn: 'Hard',
    avgWaitTime: 90,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'TOUJOURS avoir au moins 5 litres d\'eau (desert)',
      'Le 2eme pays le moins densement peuple au monde',
      'Les routes principales (B1, B2) ont un trafic modere',
      'Le ciel etoile namibien est parmi les plus purs au monde',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '10111', ambulance: '211111', fire: '211111', universal: '10111' },
  },
  {
    code: 'KE',
    name: 'Kenya',
    nameEn: 'Kenya',
    nameEs: 'Kenia',
    nameDe: 'Kenia',
    flag: '🇰🇪',
    legality: 'legal',
    legalityText: 'Pas de réglementation specifique. Le transport informel (matatus) est le mode principal.',
    legalityTextEn: 'No specific regulation. Informal transport (matatus) is the main mode.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 25,
    bestMonths: [1, 2, 7, 8, 9],
    tips: [
      'Les matatus (minibus) sont partout et tres bon marche',
      'Clarifie "free" ou "bure" avant de monter',
      'Evite le nord-est (frontiere somalienne)',
      'Hakuna matata est une vraie expression, pas juste un film !',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '999', ambulance: '999', fire: '999', universal: '112' },
  },
  {
    code: 'ET',
    name: 'Éthiopie',
    nameEn: 'Ethiopia',
    nameEs: 'Etiopía',
    nameDe: 'Äthiopien',
    flag: '🇪🇹',
    legality: 'legal',
    legalityText: 'Légal. Le transport informel (camions) est courant.',
    legalityTextEn: 'Legal. Informal transport (trucks) is common.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [10, 11, 12, 1, 2, 3],
    tips: [
      'L\'Ethiopie utilise son propre calendrier (13 mois, 7-8 ans de decalage)',
      'La ceremonie du cafe est un rituel social de 30-60 minutes',
      'Clarifie "free, no birr" avant de monter (transport payant courant)',
      'Lalibela, Gondar et l\'Omo Valley sont les points forts',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '991', ambulance: '907', fire: '939', universal: '911' },
  },
  // ==================== MORE ASIA ====================
  {
    code: 'MM',
    name: 'Myanmar',
    nameEn: 'Myanmar',
    nameEs: 'Myanmar',
    nameDe: 'Myanmar',
    flag: '🇲🇲',
    legality: 'legal',
    legalityText: 'Pas de réglementation. Les conducteurs s\'arretent facilement. Verifiez la situation politique.',
    legalityTextEn: 'No regulation. Drivers stop easily. Check the political situation.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 25,
    bestMonths: [11, 12, 1, 2],
    tips: [
      'Verifiez la situation securitaire avant de voyager (coup d\'Etat 2021)',
      'Les Birmans sont incroyablement accueillants',
      'Le thanaka (pate jaune sur le visage) est un cosmetique traditionnel',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '199', ambulance: '192', fire: '191', universal: '199' },
  },
  {
    code: 'ID',
    name: 'Indonésie',
    nameEn: 'Indonesia',
    nameEs: 'Indonesia',
    nameDe: 'Indonesien',
    flag: '🇮🇩',
    legality: 'legal',
    legalityText: 'Le transport informel est la norme. Le stop classique est rare mais les gens aident.',
    legalityTextEn: 'Informal transport is the norm. Classic hitchhiking is rare but people help.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [5, 6, 7, 8, 9],
    tips: [
      'Le plus grand archipel du monde (17 000 iles)',
      'Les ojeks (motos-taxis) sont partout',
      'Le bahasa indonesia est facile a apprendre',
      'Bali n\'est pas representatif de l\'Indonesie',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '110', ambulance: '118', fire: '113', universal: '112' },
  },
  {
    code: 'PH',
    name: 'Philippines',
    nameEn: 'Philippines',
    nameEs: 'Filipinas',
    nameDe: 'Philippinen',
    flag: '🇵🇭',
    legality: 'legal',
    legalityText: 'Pas de réglementation. Les Filipinos sont parmi les gens les plus hospitaliers d\'Asie.',
    legalityTextEn: 'No regulation. Filipinos are among the most hospitable people in Asia.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [12, 1, 2, 3, 4],
    tips: [
      'L\'anglais est tres bien parle (3eme pays anglophone)',
      'Les jeepneys sont le transport iconique et tres bon marche',
      'Evite l\'ouest de Mindanao (conflit arme)',
      'Les Filipinos adorent le karaoke et les fetes',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  {
    code: 'LK',
    name: 'Sri Lanka',
    nameEn: 'Sri Lanka',
    nameEs: 'Sri Lanka',
    nameDe: 'Sri Lanka',
    flag: '🇱🇰',
    legality: 'legal',
    legalityText: 'Légal et facile. Les Sri Lankais sont accueillants et s\'arretent facilement.',
    legalityTextEn: 'Legal and easy. Sri Lankans are welcoming and stop easily.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 15,
    bestMonths: [12, 1, 2, 3],
    tips: [
      'Le train Kandy-Ella est parmi les plus beaux trajets au monde',
      'Le rice & curry est le plat quotidien',
      'Le pays est petit (430 km) et se traverse facilement',
      'Le the de Ceylan est une fierte nationale',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '119', ambulance: '110', fire: '111', universal: '112' },
  },
  {
    code: 'MN',
    name: 'Mongolie',
    nameEn: 'Mongolia',
    nameEs: 'Mongolia',
    nameDe: 'Mongolei',
    flag: '🇲🇳',
    legality: 'legal',
    legalityText: 'Légal. En dehors d\'Oulan-Bator, tout vehicule s\'arrete car le trafic est tres faible.',
    legalityTextEn: 'Legal. Outside Ulaanbaatar, every vehicle stops as traffic is very low.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 45,
    bestMonths: [6, 7, 8],
    tips: [
      'Les yourtes des nomades sont ouvertes aux voyageurs (tradition sacree)',
      'En dehors d\'UB, il n\'y a quasiment pas de routes goudronees, ce sont des pistes',
      'Le koumiss (lait de jument fermente) est la boisson traditionnelle',
      'Le Naadam (juillet) est le festival national : lutte, tir a l\'arc, chevaux',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '102' },
  },
  {
    code: 'KR',
    name: 'Corée du Sud',
    nameEn: 'South Korea',
    nameEs: 'Corea del Sur',
    nameDe: 'Südkorea',
    flag: '🇰🇷',
    legality: 'legal',
    legalityText: 'Légal mais rare. Les transports publics sont excellents et bon marche.',
    legalityTextEn: 'Legal but rare. Public transport is excellent and cheap.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 40,
    bestMonths: [4, 5, 9, 10],
    tips: [
      'Les jusoyuso (stations-service) sur les autoroutes sont les meilleurs spots',
      'Les Coreens qui s\'arretent sont tres enthousiastes et genereux',
      'Les jjimjilbangs (saunas) offrent un hebergement pas cher',
      'Le hangul (alphabet coreen) s\'apprend en quelques heures',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '112', ambulance: '119', fire: '119', universal: '112' },
  },
  // ==================== MORE AMERICAS ====================
  {
    code: 'CU',
    name: 'Cuba',
    nameEn: 'Cuba',
    nameEs: 'Cuba',
    nameDe: 'Kuba',
    flag: '🇨🇺',
    legality: 'legal',
    legalityText: 'Légal et organise par l\'Etat. Les vehicules d\'Etat sont obliges de prendre des autostoppeurs.',
    legalityTextEn: 'Legal and organized by the state. State vehicles are required to pick up hitchhikers.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 15,
    bestMonths: [11, 12, 1, 2, 3, 4],
    tips: [
      'Les "amarillos" (fonctionnaires en jaune) organisent le stop officiellement',
      'Les vehicules d\'Etat sont OBLIGES par la loi de prendre des stoppeurs',
      'Les cartes bancaires americaines ne fonctionnent PAS',
      'Apporte des euros en cash, le change officiel est defavorable',
    ],
    laws: ['Legal et organise par l\'Etat via les "puntos de botella"'],
    emergencyNumbers: { police: '106', ambulance: '104', fire: '105', universal: '106' },
  },
  {
    code: 'GT',
    name: 'Guatemala',
    nameEn: 'Guatemala',
    nameEs: 'Guatemala',
    nameDe: 'Guatemala',
    flag: '🇬🇹',
    legality: 'legal',
    legalityText: 'Légal. Les pickups sont le transport principal en zone rurale.',
    legalityTextEn: 'Legal. Pickups are the main transport in rural areas.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [11, 12, 1, 2, 3, 4],
    tips: [
      'Les chicken buses (anciens bus scolaires US) sont une experience unique',
      'Le Lago Atitlan et Antigua sont les zones les plus faciles',
      'Le coeur du monde maya avec des communautes vivantes',
      'Le cafe guatemalteque est parmi les meilleurs au monde',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '120', ambulance: '123', fire: '110', universal: '110' },
  },
  {
    code: 'CR',
    name: 'Costa Rica',
    nameEn: 'Costa Rica',
    nameEs: 'Costa Rica',
    nameDe: 'Costa Rica',
    flag: '🇨🇷',
    legality: 'legal',
    legalityText: 'Légal et facile. Pura vida ! Le pays le plus sur d\'Amerique centrale.',
    legalityTextEn: 'Legal and easy. Pura vida! The safest country in Central America.',
    difficulty: 2,
    difficultyText: 'Facile',
    difficultyTextEn: 'Easy',
    avgWaitTime: 20,
    bestMonths: [12, 1, 2, 3, 4],
    tips: [
      'Pura vida = bonjour, au revoir, merci, ca va, tout va bien',
      'Le pays le plus sur d\'Amerique centrale, pas d\'armee depuis 1949',
      '25% du territoire est en reserve naturelle',
      'Les surfeurs s\'arretent facilement sur les routes du Pacifique',
    ],
    laws: ['Aucune restriction legale'],
    emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  // ==================== MORE AFRICA / MIDDLE EAST ====================
  {
    code: 'EG',
    name: 'Égypte',
    nameEn: 'Egypt',
    nameEs: 'Egipto',
    nameDe: 'Ägypten',
    flag: '🇪🇬',
    legality: 'legal',
    legalityText: 'Le transport informel (microbuses) est le mode principal. Le stop gratuit fonctionne avec les camions.',
    legalityTextEn: 'Informal transport (microbuses) is the main mode. Free hitchhiking works with trucks.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 20,
    bestMonths: [10, 11, 12, 1, 2, 3],
    tips: [
      'Les microbuses sont partout et coutent presque rien',
      'Le koshari (plat national) coute 0,50-1 €',
      'Clarifie "mish flous" (pas d\'argent) avant de monter',
      'Les Pyramides, Louxor et le Nil sont des merveilles eternelles',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '122', ambulance: '123', fire: '180', universal: '112' },
  },
  {
    code: 'TZ',
    name: 'Tanzanie',
    nameEn: 'Tanzania',
    nameEs: 'Tanzania',
    nameDe: 'Tansania',
    flag: '🇹🇿',
    legality: 'legal',
    legalityText: 'Pas de réglementation. Les dala-dala (minibus) sont le transport principal.',
    legalityTextEn: 'No regulation. Dala-dala (minibuses) are the main transport.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 25,
    bestMonths: [6, 7, 8, 9, 1, 2],
    tips: [
      'Le Serengeti, le Kilimandjaro et Zanzibar sont les points forts',
      'Clarifie "bure" (gratuit) avant de monter',
      'Les dala-dala sont tres bon marche mais bondes',
      'Le swahili est la langue du quotidien',
    ],
    laws: ['Pas de reglementation specifique'],
    emergencyNumbers: { police: '114', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'TJ',
    name: 'Tadjikistan',
    nameEn: 'Tajikistan',
    nameEs: 'Tayikistán',
    nameDe: 'Tadschikistan',
    flag: '🇹🇯',
    legality: 'legal',
    legalityText: 'Légal. La Pamir Highway est un classique mythique de l\'autostop mondial.',
    legalityTextEn: 'Legal. The Pamir Highway is a legendary hitchhiking classic.',
    difficulty: 3,
    difficultyText: 'Moyen',
    difficultyTextEn: 'Medium',
    avgWaitTime: 30,
    bestMonths: [6, 7, 8, 9],
    tips: [
      'La Pamir Highway (M41) est l\'une des plus hautes routes du monde',
      'Un GBAO permit est necessaire pour le Pamir',
      'Les Pamiri sont parmi les gens les plus accueillants de la planete',
      'Le russe est indispensable',
    ],
    laws: ['Aucune restriction legale. GBAO permit necessaire pour le Pamir.'],
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '112' },
  },
  // ==================== MORE COUNTRIES (LOT 4) ====================
  {
    code: 'PK', name: 'Pakistan', nameEn: 'Pakistan', nameEs: 'Pakistán', nameDe: 'Pakistan', flag: '🇵🇰', legality: 'legal',
    legalityText: 'Légal. L\'hospitalite pakistanaise est consideree comme la meilleure au monde.',
    legalityTextEn: 'Legal. Pakistani hospitality is considered the best in the world.',
    difficulty: 1, difficultyText: 'Tres facile', difficultyTextEn: 'Very easy', avgWaitTime: 10,
    bestMonths: [4, 5, 9, 10],
    tips: ['La Karakoram Highway est l\'une des plus belles routes du monde', 'Les conducteurs refusent systematiquement l\'argent', 'Le Hunza Valley est un paradis pour le stop', 'Le chai est offert a toute heure'],
    laws: ['Aucune restriction legale'], emergencyNumbers: { police: '15', ambulance: '1122', fire: '16', universal: '1122' },
  },
  {
    code: 'MY', name: 'Malaisie', nameEn: 'Malaysia', nameEs: 'Malasia', nameDe: 'Malaysia', flag: '🇲🇾', legality: 'legal',
    legalityText: 'Légal et facile. Les Malaisiens sont accueillants et curieux.',
    legalityTextEn: 'Legal and easy. Malaysians are welcoming and curious.',
    difficulty: 2, difficultyText: 'Facile', difficultyTextEn: 'Easy', avgWaitTime: 20,
    bestMonths: [2, 3, 7, 8],
    tips: ['Les stations Petronas sont les meilleurs spots', 'L\'anglais est tres repandu', 'Le nasi lemak est le petit-dejeuner national', 'Societe multiculturelle (malais, chinois, indiens)'],
    laws: ['Aucune restriction legale'], emergencyNumbers: { police: '999', ambulance: '999', fire: '994', universal: '999' },
  },
  {
    code: 'TW', name: 'Taiwan', nameEn: 'Taiwan', nameEs: 'Taiwán', nameDe: 'Taiwan', flag: '🇹🇼', legality: 'legal',
    legalityText: 'Légal et excellent. Les Taiwanais sont extremement accueillants.',
    legalityTextEn: 'Legal and excellent. Taiwanese are extremely welcoming.',
    difficulty: 2, difficultyText: 'Facile', difficultyTextEn: 'Easy', avgWaitTime: 15,
    bestMonths: [4, 5, 10, 11],
    tips: ['La cote est (Taroko Gorge, Hualien) est la plus scenic', 'Les Taiwanais invitent souvent a manger et dormir', 'Le bubble tea a ete invente ici', 'Premier mariage gay legalise en Asie (2019)'],
    laws: ['Aucune restriction legale'], emergencyNumbers: { police: '110', ambulance: '119', fire: '119', universal: '110' },
  },
  {
    code: 'LB', name: 'Liban', nameEn: 'Lebanon', nameEs: 'Líbano', nameDe: 'Libanon', flag: '🇱🇧', legality: 'legal',
    legalityText: 'Légal. Petit pays (170 km), facile en stop. Verifiez la situation securitaire.',
    legalityTextEn: 'Legal. Small country (170 km), easy to hitch. Check the security situation.',
    difficulty: 2, difficultyText: 'Facile', difficultyTextEn: 'Easy', avgWaitTime: 10,
    bestMonths: [4, 5, 6, 9, 10],
    tips: ['Le francais est tres repandu', 'Le service (taxi partage) coute presque rien', 'La cuisine libanaise est parmi les meilleures au monde', 'Verifiez la situation securitaire avant de voyager'],
    laws: ['Aucune restriction legale'], emergencyNumbers: { police: '112', ambulance: '140', fire: '175', universal: '112' },
  },
  {
    code: 'PA', name: 'Panama', nameEn: 'Panama', nameEs: 'Panamá', nameDe: 'Panama', flag: '🇵🇦', legality: 'legal',
    legalityText: 'Légal. Hub de transit entre les Ameriques. Le Darien Gap n\'a pas de route.',
    legalityTextEn: 'Legal. Transit hub between the Americas. The Darien Gap has no road.',
    difficulty: 2, difficultyText: 'Facile', difficultyTextEn: 'Easy', avgWaitTime: 20,
    bestMonths: [12, 1, 2, 3],
    tips: ['Dollar americain, plus cher que les voisins', 'Le Darien Gap = jungle sans route vers la Colombie', 'Bocas del Toro et Boquete sont des zones de backpackers', 'Les iles San Blas (Kunas) sont un paradis'],
    laws: ['Aucune restriction legale'], emergencyNumbers: { police: '911', ambulance: '911', fire: '911', universal: '911' },
  },
  {
    code: 'GH', name: 'Ghana', nameEn: 'Ghana', nameEs: 'Ghana', nameDe: 'Ghana', flag: '🇬🇭', legality: 'legal',
    legalityText: 'L\'un des pays les plus stables d\'Afrique. Les tro-tros sont le transport principal.',
    legalityTextEn: 'One of the most stable countries in Africa. Tro-tros are the main transport.',
    difficulty: 2, difficultyText: 'Facile', difficultyTextEn: 'Easy', avgWaitTime: 20,
    bestMonths: [11, 12, 1, 2, 3],
    tips: ['L\'anglais est la langue officielle et largement parle', 'Les tro-tros sont omniprésents et bon marché', 'Le jollof rice est une fierté nationale', '"Akwaaba" = bienvenue en twi'],
    laws: ['Pas de reglementation specifique'], emergencyNumbers: { police: '191', ambulance: '193', fire: '192', universal: '112' },
  },
  {
    code: 'UG', name: 'Ouganda', nameEn: 'Uganda', nameEs: 'Uganda', nameDe: 'Uganda', flag: '🇺🇬', legality: 'legal',
    legalityText: '"The Pearl of Africa". Transport informel (boda-bodas, matatus) omniprésent.',
    legalityTextEn: '"The Pearl of Africa". Informal transport (boda-bodas, matatus) everywhere.',
    difficulty: 3, difficultyText: 'Moyen', difficultyTextEn: 'Medium', avgWaitTime: 25,
    bestMonths: [1, 2, 6, 7, 8, 9],
    tips: ['Les gorilles de Bwindi sont l\'attraction phare (700 $/permis)', 'Les boda-bodas sont partout mais dangereux', 'Le rolex (chapati+omelette) est le street food national', 'L\'anglais est bien parle'],
    laws: ['Pas de reglementation specifique'], emergencyNumbers: { police: '999', ambulance: '112', fire: '999', universal: '112' },
  },
  {
    code: 'RW', name: 'Rwanda', nameEn: 'Rwanda', nameEs: 'Ruanda', nameDe: 'Ruanda', flag: '🇷🇼', legality: 'legal',
    legalityText: 'Le pays le plus propre et organise d\'Afrique. Tres sur.',
    legalityTextEn: 'The cleanest and most organized country in Africa. Very safe.',
    difficulty: 3, difficultyText: 'Moyen', difficultyTextEn: 'Medium', avgWaitTime: 25,
    bestMonths: [1, 2, 6, 7, 8, 9],
    tips: ['Le pays le plus propre d\'Afrique (sacs plastiques interdits)', 'Gorilles de montagne (Volcanoes NP) : 1 500 $/permis', 'Le plus haut taux de femmes au parlement au monde', 'Le cafe rwandais est excellent'],
    laws: ['Pas de reglementation specifique'], emergencyNumbers: { police: '112', ambulance: '112', fire: '112', universal: '112' },
  },
  {
    code: 'MW', name: 'Malawi', nameEn: 'Malawi', nameEs: 'Malaui', nameDe: 'Malawi', flag: '🇲🇼', legality: 'legal',
    legalityText: '"The warm heart of Africa". Accueil exceptionnel.',
    legalityTextEn: '"The warm heart of Africa". Exceptional welcome.',
    difficulty: 2, difficultyText: 'Facile', difficultyTextEn: 'Easy', avgWaitTime: 20,
    bestMonths: [5, 6, 7, 8, 9, 10],
    tips: ['Les Malawites sont consideres comme les gens les plus accueillants d\'Afrique', 'Le lac Malawi est un paradis d\'eau douce', 'Le ferry Ilala est une experience mythique (3 jours)', 'Le nsima est le plat quotidien'],
    laws: ['Aucune restriction legale'], emergencyNumbers: { police: '997', ambulance: '998', fire: '999', universal: '997' },
  },
  // ==================== ADDITIONAL COUNTRIES (with sections in guideSections.js) ====================
  {
    code: 'AL',
    name: 'Albanie',
    nameEn: 'Albania',
    nameEs: 'Albania',
    nameDe: 'Albanien',
    flag: '🇦🇱',
    legality: 'legal',
    legalityText: 'Aucune loi contre l\'autostop. Pratique courante et culturellement acceptee.',
    legalityTextEn: 'No law against hitchhiking. Common practice and culturally accepted.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 15,
    bestMonths: [4, 5, 6, 9, 10],
    tips: [
      'Dis "autostop, jo lek" (pas d\'argent) pour eviter les confusions avec les taxis informels',
      'L\'hospitalite albanaise est legendaire, les conducteurs font des detours pour t\'aider',
      'Les furgons (minibus) sont le transport principal, leve la main pour les arreter',
      'Attention aux "mafias de taxis" pres de la frontiere grecque (Kakavia)',
      'Les plages au nord de Durres sont adaptees au camping sauvage',
      'Le concept de "besa" (code d\'honneur) protege les voyageurs',
    ],
    tipsEn: [
      'Say "autostop, jo lek" (no money) to avoid confusion with informal taxis',
      'Albanian hospitality is legendary, drivers go out of their way to help you',
      'Furgons (minivans) are the main transport, wave them down',
      'Beware of "taxi mafias" near the Greek border (Kakavia)',
      'Beaches north of Durres are suitable for wild camping',
      'The concept of "besa" (code of honor) protects travelers',
    ],
    laws: [
      'Aucune restriction legale connue',
    ],
    emergencyNumbers: { police: '129', ambulance: '127', fire: '128', universal: '112' },
  },
  {
    code: 'AM',
    name: 'Arménie',
    nameEn: 'Armenia',
    nameEs: 'Armenia',
    nameDe: 'Armenien',
    flag: '🇦🇲',
    legality: 'legal',
    legalityText: 'Légal et extremement facile. L\'Armenie est classee parmi les meilleurs pays au monde pour l\'autostop.',
    legalityTextEn: 'Legal and extremely easy. Armenia is ranked among the best countries in the world for hitchhiking.',
    difficulty: 1,
    difficultyText: 'Tres facile',
    difficultyTextEn: 'Very easy',
    avgWaitTime: 8,
    bestMonths: [4, 5, 6, 9, 10],
    tips: [
      'Temps d\'attente moyen 5-10 minutes, parfois moins',
      'Pouce leve = autostop, paume vers le bas = taxi. Clarifie AVANT de monter.',
      'Les conducteurs offrent souvent cola, glace ou repas complets',
      'Le russe est essentiel en dehors d\'Erevan',
      'Budget ultra-bas possible : 7 euros/jour',
      'Evite les zones frontalieres avec l\'Azerbaidjan (mines, tensions)',
    ],
    tipsEn: [
      'Average wait time 5-10 minutes, sometimes less',
      'Thumb up = hitchhike, palm down = taxi. Clarify BEFORE getting in.',
      'Drivers often offer cola, ice cream or full meals',
      'Russian is essential outside Yerevan',
      'Ultra-low budget possible: 7 EUR/day',
      'Avoid border areas with Azerbaijan (mines, tensions)',
    ],
    laws: [
      'Aucune restriction. Utilise le pouce leve (paume vers le bas = taxi).',
    ],
    emergencyNumbers: { police: '102', ambulance: '103', fire: '101', universal: '112' },
  },
]

/**
 * Enrich guides with sections data for the current language
 * Call this after loadGuideSections() resolves
 */
export function enrichGuidesWithSections() {
  const sectionsData = getGuideSections()
  if (!sectionsData) return
  for (const guide of countryGuides) {
    // Clear previous language sections first
    guide.sections = sectionsData[guide.code] || undefined
  }
}

/**
 * Load sections for current language and enrich guides
 */
export async function initGuideSections() {
  await loadGuideSections()
  enrichGuidesWithSections()
}

/**
 * Get guide by country code
 */
export function getGuideByCode(code) {
  return countryGuides.find(g => g.code === code)
}

/**
 * Get all guides sorted by difficulty
 */
export function getGuidesByDifficulty() {
  return [...countryGuides].sort((a, b) => a.difficulty - b.difficulty)
}

/**
 * Get easiest countries
 */
export function getEasiestCountries(limit = 5) {
  return getGuidesByDifficulty().slice(0, limit)
}

/**
 * Get guides by legality
 */
export function getGuidesByLegality(legality) {
  return countryGuides.filter(g => g.legality === legality)
}

/**
 * Search guides
 */
export function searchGuides(query) {
  const lowerQuery = query.toLowerCase()
  return countryGuides.filter(
    g =>
      g.name.toLowerCase().includes(lowerQuery) ||
      (g.nameEn && g.nameEn.toLowerCase().includes(lowerQuery)) ||
      g.code.toLowerCase().includes(lowerQuery)
  )
}

/**
 * Get all countries as a simple list
 */
export function getCountryList() {
  return countryGuides.map(g => ({
    code: g.code,
    name: g.name,
    nameEn: g.nameEn || g.name,
    flag: g.flag,
  }))
}

/**
 * Get emergency numbers for a country
 */
export function getEmergencyNumbers(countryCode) {
  const guide = getGuideByCode(countryCode)
  return guide?.emergencyNumbers || { universal: '112' }
}

/**
 * Country code → primary language mapping
 */
const COUNTRY_LANG = {
  FR: 'fr', BE: 'fr', LU: 'fr', CH: 'fr', // French-speaking
  DE: 'de', AT: 'de', // German-speaking
  ES: 'es', // Spanish
  IT: 'it', // Italian
  NL: 'nl', // Dutch
  PT: 'pt', // Portuguese
  PL: 'pl', CZ: 'cs', SK: 'sk',
  HU: 'hu', RO: 'ro', BG: 'bg', GR: 'el',
  HR: 'hr', SI: 'sl', BA: 'bs', RS: 'sr', ME: 'sr', MK: 'mk', AL: 'sq', XK: 'sq',
  SE: 'sv', NO: 'no', DK: 'da', FI: 'fi', IS: 'is',
  IE: 'en', GB: 'en', US: 'en', CA: 'en', AU: 'en', NZ: 'en',
  LT: 'lt', LV: 'lv', EE: 'et', UA: 'uk', MD: 'ro',
  TR: 'tr', IL: 'he', MA: 'ar', IR: 'fa',
  GE: 'ka', AM: 'hy',
  TH: 'th', IN: 'hi', JP: 'ja', KR: 'ko',
  AR: 'es', CL: 'es', CO: 'es', MX: 'es', PE: 'es', BO: 'es', EC: 'es', UY: 'es',
  GT: 'es', CR: 'es', PA: 'es', CU: 'es',
  BR: 'pt', ZA: 'en', GH: 'en', KE: 'en', UG: 'en', RW: 'en', MW: 'en',
  BY: 'ru', KZ: 'ru', KG: 'ru', TJ: 'ru', UZ: 'uz',
  TN: 'ar', EG: 'ar', JO: 'ar', OM: 'ar', LB: 'ar',
  SN: 'fr', ET: 'am', TZ: 'sw', NA: 'en',
  VN: 'vi', LA: 'lo', KH: 'km', MM: 'my', ID: 'id', PH: 'en', MY: 'ms',
  LK: 'si', NP: 'ne', PK: 'ur', MN: 'mn', TW: 'zh',
}

/**
 * 5 universal hitchhiking phrases in ~40 languages
 * Format: { hello, hitchhiking, goingTo, thanks, dropOff }
 */
const UNIVERSAL_PHRASES = {
  fr: { hello: 'Bonjour !', hitchhiking: 'Je fais de l\'auto-stop', goingTo: 'Vous allez vers [ville] ?', thanks: 'Merci beaucoup, bon voyage !', dropOff: 'Vous pouvez me déposer ici, s\'il vous plaît ?' },
  en: { hello: 'Hello!', hitchhiking: 'I\'m hitchhiking', goingTo: 'Are you going to [city]?', thanks: 'Thank you so much, safe travels!', dropOff: 'Can you drop me off here, please?' },
  de: { hello: 'Hallo!', hitchhiking: 'Ich trampe', goingTo: 'Fahren Sie Richtung [Stadt]?', thanks: 'Vielen Dank, gute Fahrt!', dropOff: 'Können Sie mich hier rauslassen, bitte?' },
  es: { hello: '¡Hola!', hitchhiking: 'Hago autostop', goingTo: '¿Vas hacia [ciudad]?', thanks: '¡Muchas gracias, buen viaje!', dropOff: '¿Me puedes dejar aquí, por favor?' },
  it: { hello: 'Ciao!', hitchhiking: 'Faccio autostop', goingTo: 'Va verso [città]?', thanks: 'Grazie mille, buon viaggio!', dropOff: 'Mi può lasciare qui, per favore?' },
  nl: { hello: 'Hallo!', hitchhiking: 'Ik lift', goingTo: 'Gaat u richting [stad]?', thanks: 'Heel erg bedankt, goede reis!', dropOff: 'Kunt u me hier afzetten, alstublieft?' },
  pt: { hello: 'Olá!', hitchhiking: 'Estou a fazer boleia', goingTo: 'Vai para [cidade]?', thanks: 'Muito obrigado, boa viagem!', dropOff: 'Pode me deixar aqui, por favor?' },
  pl: { hello: 'Cześć!', hitchhiking: 'Jadę autostopem', goingTo: 'Jedzie Pan/Pani w stronę [miasto]?', thanks: 'Dziękuję bardzo, szczęśliwej drogi!', dropOff: 'Może mnie Pan/Pani wysadzić tutaj?' },
  cs: { hello: 'Ahoj!', hitchhiking: 'Stopuji', goingTo: 'Jedete směrem na [město]?', thanks: 'Moc děkuji, šťastnou cestu!', dropOff: 'Můžete mě tady vysadit, prosím?' },
  sk: { hello: 'Ahoj!', hitchhiking: 'Stopujem', goingTo: 'Idete smerom na [mesto]?', thanks: 'Ďakujem veľmi pekne, šťastnú cestu!', dropOff: 'Môžete ma tu vysadiť, prosím?' },
  hu: { hello: 'Szia!', hitchhiking: 'Stoppolok', goingTo: '[Város] felé megy?', thanks: 'Köszönöm szépen, jó utat!', dropOff: 'Le tudna tenni itt, kérem?' },
  ro: { hello: 'Bună!', hitchhiking: 'Fac autostopul', goingTo: 'Mergeți spre [oraș]?', thanks: 'Mulțumesc foarte mult, drum bun!', dropOff: 'Mă puteți lăsa aici, vă rog?' },
  bg: { hello: 'Здравейте!', hitchhiking: 'Пътувам на автостоп', goingTo: 'Пътувате ли към [град]?', thanks: 'Благодаря много, лек път!', dropOff: 'Можете ли да ме оставите тук, моля?' },
  el: { hello: 'Γεια σας!', hitchhiking: 'Κάνω ωτοστόπ', goingTo: 'Πηγαίνετε προς [πόλη];', thanks: 'Ευχαριστώ πολύ, καλό ταξίδι!', dropOff: 'Μπορείτε να με αφήσετε εδώ, παρακαλώ;' },
  hr: { hello: 'Bok!', hitchhiking: 'Stopiram', goingTo: 'Idete li prema [gradu]?', thanks: 'Hvala puno, sretan put!', dropOff: 'Možete li me ostaviti ovdje, molim?' },
  sl: { hello: 'Živjo!', hitchhiking: 'Štopal/a sem', goingTo: 'Ali greste proti [mestu]?', thanks: 'Hvala lepa, srečno pot!', dropOff: 'Me lahko spustite tukaj, prosim?' },
  bs: { hello: 'Zdravo!', hitchhiking: 'Stopiram', goingTo: 'Idete li prema [gradu]?', thanks: 'Hvala puno, sretan put!', dropOff: 'Možete li me ostaviti ovdje, molim?' },
  sr: { hello: 'Zdravo!', hitchhiking: 'Stopiram', goingTo: 'Idete li ka [gradu]?', thanks: 'Hvala puno, srećan put!', dropOff: 'Možete li da me ostavite ovde, molim?' },
  mk: { hello: 'Здраво!', hitchhiking: 'Стопирам', goingTo: 'Одите ли кон [град]?', thanks: 'Благодарам многу, среќен пат!', dropOff: 'Можете ли да ме оставите тука, ве молам?' },
  sq: { hello: 'Përshëndetje!', hitchhiking: 'Po bëj autostop', goingTo: 'A shkoni drejt [qytetit]?', thanks: 'Faleminderit shumë, rrugë të mbarë!', dropOff: 'A mund të më lini këtu, ju lutem?' },
  sv: { hello: 'Hej!', hitchhiking: 'Jag liftar', goingTo: 'Åker du mot [stad]?', thanks: 'Tack så mycket, trevlig resa!', dropOff: 'Kan du släppa av mig här, tack?' },
  no: { hello: 'Hei!', hitchhiking: 'Jeg haiker', goingTo: 'Kjører du mot [by]?', thanks: 'Tusen takk, god tur!', dropOff: 'Kan du slippe meg av her, vær så snill?' },
  da: { hello: 'Hej!', hitchhiking: 'Jeg blaffer', goingTo: 'Kører du mod [by]?', thanks: 'Tusind tak, god tur!', dropOff: 'Kan du sætte mig af her, tak?' },
  fi: { hello: 'Hei!', hitchhiking: 'Liftaan', goingTo: 'Menetkö kohti [kaupunkia]?', thanks: 'Kiitos paljon, hyvää matkaa!', dropOff: 'Voisitko jättää minut tähän, kiitos?' },
  is: { hello: 'Hæ!', hitchhiking: 'Ég er að pútta', goingTo: 'Ertu á leið til [borg]?', thanks: 'Takk kærlega, góða ferð!', dropOff: 'Geturðu sleppt mér hér, vinsamlegast?' },
  lt: { hello: 'Labas!', hitchhiking: 'Keliauju autostopu', goingTo: 'Ar važiuojate link [miesto]?', thanks: 'Labai ačiū, gero kelio!', dropOff: 'Ar galite mane čia išleisti, prašau?' },
  lv: { hello: 'Sveiki!', hitchhiking: 'Es stopēju', goingTo: 'Vai jūs braucat uz [pilsētas] pusi?', thanks: 'Liels paldies, labu ceļu!', dropOff: 'Vai varat mani izlaist šeit, lūdzu?' },
  et: { hello: 'Tere!', hitchhiking: 'Ma hääletasin', goingTo: 'Kas te sõidate [linna] suunas?', thanks: 'Suur tänu, head teed!', dropOff: 'Kas saate mind siin maha lasta, palun?' },
  uk: { hello: 'Привіт!', hitchhiking: 'Я їду автостопом', goingTo: 'Ви їдете в бік [місто]?', thanks: 'Дуже дякую, щасливої дороги!', dropOff: 'Ви можете висадити мене тут, будь ласка?' },
  tr: { hello: 'Merhaba!', hitchhiking: 'Otostop yapıyorum', goingTo: '[Şehir] yönüne mi gidiyorsunuz?', thanks: 'Çok teşekkürler, iyi yolculuklar!', dropOff: 'Beni burada bırakabilir misiniz, lütfen?' },
  he: { hello: '!שלום', hitchhiking: 'אני עושה טרמפ', goingTo: '?אתה נוסע לכיוון [עיר]', thanks: '!תודה רבה, נסיעה טובה', dropOff: '?אפשר להוריד אותי פה, בבקשה' },
  ar: { hello: '!مرحبا', hitchhiking: 'أنا أسافر بالتوقف', goingTo: 'هل تذهب باتجاه [مدينة]؟', thanks: '!شكرا جزيلا، رحلة سعيدة', dropOff: 'هل يمكنك إنزالي هنا، من فضلك؟' },
  fa: { hello: '!سلام', hitchhiking: 'من اتواستاپ می‌کنم', goingTo: 'آیا به سمت [شهر] می‌روید؟', thanks: '!خیلی ممنون، سفر خوبی داشته باشید', dropOff: 'می‌توانید مرا اینجا پیاده کنید، لطفاً؟' },
  ka: { hello: 'გამარჯობა!', hitchhiking: 'ავტოსტოპით ვმოგზაურობ', goingTo: '[ქალაქის] მიმართულებით მიდიხართ?', thanks: 'დიდი მადლობა, კარგ მგზავრობას!', dropOff: 'შეგიძლიათ აქ ჩამომსვათ, გთხოვთ?' },
  hy: { hello: 'Barev!', hitchhiking: 'Yes autostop em anum', goingTo: '[city] gnumek?', thanks: 'Shnorhakalutyun, bari janapar!', dropOff: 'Karogh ek intz aystex thoghnel?' },
  th: { hello: 'สวัสดี!', hitchhiking: 'ฉันโบกรถ', goingTo: 'คุณไป [เมือง] ไหม?', thanks: 'ขอบคุณมาก เดินทางปลอดภัย!', dropOff: 'ช่วยจอดตรงนี้ได้ไหมครับ/คะ?' },
  hi: { hello: 'नमस्ते!', hitchhiking: 'मैं हिचहाइकिंग कर रहा/रही हूँ', goingTo: 'क्या आप [शहर] की तरफ जा रहे हैं?', thanks: 'बहुत धन्यवाद, शुभ यात्रा!', dropOff: 'क्या आप मुझे यहाँ उतार सकते हैं, कृपया?' },
  ja: { hello: 'こんにちは！', hitchhiking: 'ヒッチハイクしています', goingTo: '[都市]の方に行きますか？', thanks: 'ありがとうございます、良い旅を！', dropOff: 'ここで降ろしてもらえますか？' },
  ko: { hello: '안녕하세요!', hitchhiking: '히치하이킹 중이에요', goingTo: '[도시] 방향으로 가시나요?', thanks: '정말 감사합니다, 좋은 여행 되세요!', dropOff: '여기서 내려주실 수 있나요?' },
}

/**
 * Get 5 universal phrases for a country
 * @param {string} countryCode - ISO country code
 * @returns {Array<{local: string, meaning: string, meaningEn: string}>}
 */
export function getUniversalPhrases(countryCode) {
  const lang = COUNTRY_LANG[countryCode] || 'en'
  const phrases = UNIVERSAL_PHRASES[lang] || UNIVERSAL_PHRASES.en
  const fr = UNIVERSAL_PHRASES.fr
  const en = UNIVERSAL_PHRASES.en

  return [
    { local: phrases.hello, meaning: fr.hello, meaningEn: en.hello },
    { local: phrases.hitchhiking, meaning: fr.hitchhiking, meaningEn: en.hitchhiking },
    { local: phrases.goingTo, meaning: fr.goingTo, meaningEn: en.goingTo },
    { local: phrases.thanks, meaning: fr.thanks, meaningEn: en.thanks },
    { local: phrases.dropOff, meaning: fr.dropOff, meaningEn: en.dropOff },
  ]
}

export default {
  countryGuides,
  getGuideByCode,
  getGuidesByDifficulty,
  getEasiestCountries,
  getGuidesByLegality,
  searchGuides,
  getCountryList,
  getEmergencyNumbers,
}
